// Dashboard.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import TopToolbar from "./TopToolbar";
import ChatFab from "../chat/ChatFab";
import { CHAT_ENABLED, fetch988Comparison, fetch988Indicator, fetch988Trend, fetchNsduhTreatmentSeries, fetchOutcomeBreakdown, fetchOutcomeOptions, fetchOutcomeTrend, fetchSuicideOutcomes } from "../../api";
import { NSDUH_TREATMENT_SERIES } from "../../data/nsduhTreatmentSeries";
import "./dashboard-theme.css";

const NAV_COPY = {
  overview: {
    title: "Strategic Direction 2",
    description: "Treatment and Crisis Services prototype package overview.",
  },
  aboutUs: {
    title: "About Us",
  },
  dataDashboard: {
    title: "Data Dashboard",
    description: "Move between a national data overview and state trend comparisons.",
  },
  resources: {
    title: "Resources",
    description: "Browse federal and state suicide prevention resources.",
  },
  stories: {
    title: "Stories of Hope",
  },
};

const CHAT_PINNED_MESSAGES = {
  overview: "Use this overview to frame the prototype package, metric readiness, and the Strategic Direction 2 funding story before drilling into the data.",
  aboutUs: "Use About Us to learn more about the Action Alliance's progress, accountability, and data work.",
  dataDashboard: "Use the Data Dashboard to move between the national overview and trend comparisons.",
  resources: "Ask about care pathways, crisis resources, outreach planning, and follow-up workflows.",
  stories: "Ask about lived-experience stories, hope, recovery, and how storytelling can support prevention work.",
};
const DASHBOARD_SECTION_NAV = [
  { id: "overview", label: "Introduction" },
  { id: "dataDashboard", label: "Data Dashboard" },
  { id: "resources", label: "Resources" },
  { id: "stories", label: "Stories of Hope" },
  { id: "aboutUs", label: "About Us" },
];
const ABOUT_US_URL = "https://theactionalliance.org/";
const NATIONAL_STRATEGY_URL = "https://www.hhs.gov/programs/prevention-and-wellness/mental-health-substance-use-disorder/national-strategy-suicide-prevention/index.html";
const DISC_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/disc_logo.webp`;
const DASHBOARD_TOUR_STORAGE_KEY = "dashboard_intro_tour_seen";
const DASHBOARD_TOUR_STEPS = [
  {
    id: "nav",
    target: ".dashboard-tabs",
    scrollTarget: ".dashboard-topbar",
    title: "Top navigation",
    body: "Introduction, Data Dashboard, Resources, and Stories of Hope jump to the main areas of the page.",
    padding: 8,
  },
  {
    id: "rail",
    target: ".section-rail",
    title: "Section rail",
    body: "Use this side rail to scroll to the matching page section and keep your place as you move through the dashboard.",
    padding: 10,
  },
  {
    id: "assistant",
    target: ".chat-fab-button",
    title: "Help button",
    body: "Open the assistant here when you want help interpreting the dashboard, selected section, or data context. The expanded panel also includes a Restart tutorial action in case you want to revisit this walkthrough.",
    padding: 12,
  },
];
const DATA_DASHBOARD_VIEWS = [
  {
    id: "overview",
    label: "Overview",
    title: "Overview",
    description: "",
  },
  {
    id: "trends",
    label: "Trends",
    title: "Trends",
    description: "",
  },
  {
    id: "compare",
    label: "Compare",
    title: "Compare",
    description: "",
  },
];
const OVERVIEW_METRIC_IMAGES = [
  "/assets/overview-metric-row-1.png",
  "/assets/overview-metric-row-2.png",
  "/assets/overview-metric-row-3.png",
  "/assets/overview-metric-row-4.png",
  "/assets/overview-metric-row-5.png",
];

const OVERVIEW_METRIC_ROWS = [
  {
    imageLabel: "Impact, access, and treatment",
    image: OVERVIEW_METRIC_IMAGES[0],
    metrics: [
      {
        eyebrow: "Impact Data",
        title: "Suicide mortality rate, age-adjusted",
        body: "Core outcome measure for national and state trend views; interpretable for public and funder audiences.",
        dataSelectionValue: "layer:suicideAdjustedRate",
        status: "Strong public source",
      },
      {
        eyebrow: "Context & Tracking",
        title: "988 Calls Answer Rate",
        body: "Public 988 reporting can support national/state comparisons and monthly performance movement.",
        dataSelectionValue: "kpi:in_state_answer_rate",
        status: "Dashboard-ready",
      },
      {
        eyebrow: "Coverage & Reach",
        title: "Adults with serious thoughts of suicide who received mental health treatment",
        body: "National NSDUH estimates of any past-year mental health treatment among adults reporting serious thoughts of suicide, with comparability caveats.",
        dataSelectionValue: NSDUH_TREATMENT_SERIES.selectionValue,
        status: "Strong national",
      },
    ],
  },
  {
    imageLabel: "Policy, workforce, and ED follow-up",
    image: OVERVIEW_METRIC_IMAGES[1],
    metrics: [
      {
        eyebrow: "Context & Tracking",
        title: "Degree of state support for 988 funding",
        body: "Turns fee and appropriation policy into a practical funding-readiness signal once scoring is defined.",
        status: "Needs scoring",
      },
      {
        eyebrow: "Context & Tracking",
        title: "Suicide prevention training for licensed clinicians",
        body: "Tracks whether states require suicide assessment, treatment, and management training in licensure policy.",
        status: "Policy tracker",
      },
      {
        eyebrow: "Care Practices",
        title: "Follow-up after ED visits, 7 and 30 days",
        body: "A familiar care-continuity measure for post-crisis connection, with suicide-specific caveats to name clearly.",
        status: "HEDIS measure",
      },
    ],
  },
  {
    imageLabel: "Crisis pathway and system capacity",
    image: OVERVIEW_METRIC_IMAGES[4],
    metrics: [
      {
        eyebrow: "Care Practices",
        title: "Follow-up after psychiatric hospitalization, 7 and 30 days",
        body: "Supports accountability for timely outpatient connection after inpatient mental health care.",
        status: "HEDIS measure",
      },
      {
        eyebrow: "Context & Tracking",
        title: "States with mobile crisis dispatch integration",
        body: "Shows whether crisis lines connect to field response, but depends on state profile definitions and cadence.",
        status: "Partial",
      },
      {
        eyebrow: "Coverage & Reach",
        title: "Population covered by mobile crisis services",
        body: "Communicates where crisis systems have places for people to go beyond call response alone.",
        status: "Partial",
      },
      {
        eyebrow: "Coverage & Reach",
        title: "Child crisis receiving/mobile crisis availability",
        body: "Adds a lifespan and equity lens to the prototype, while flagging state-reporting limitations.",
        status: "Partial",
      },
    ],
  },
];


const OVERVIEW_METRIC_ORDER = [
  "Suicide mortality rate, age-adjusted",
  "988 Calls Answer Rate",
  "Follow-up after psychiatric hospitalization, 7 and 30 days",
  "Follow-up after ED visits, 7 and 30 days",
  "Degree of state support for 988 funding",
  "States with mobile crisis dispatch integration",
  "Suicide prevention training for licensed clinicians",
  "Child crisis receiving/mobile crisis availability",
  "Adults with serious thoughts of suicide who received mental health treatment",
  "Population covered by mobile crisis services",
];

const OVERVIEW_METRICS = OVERVIEW_METRIC_ORDER.map((title) =>
  OVERVIEW_METRIC_ROWS.flatMap((row) => row.metrics).find((metric) => metric.title === title),
).filter(Boolean);

const DATA_READY_SELECTED_MEASURES = new Set([
  "Suicide mortality rate, age-adjusted",
  "988 Calls Answer Rate",
  "Adults with serious thoughts of suicide who received mental health treatment",
]);

const METRIC_SELECTION_FRAMEWORKS = [
  {
    id: "care",
    title: "Care Practices",
    prompts: ["Are we delivering the right care?", "Do people stay connected to that care over time?"],
  },
  {
    id: "context",
    title: "Context, Competency, and Tracking",
    prompts: ["Do we have the system capacity to deliver care?", "How well is that system performing?"],
  },
  {
    id: "coverage",
    title: "Coverage and Reach",
    prompts: ["Who are we reaching?", "Are we serving all communities effectively?"],
  },
];

const SCORING_CHARACTERISTICS = [
  { label: "Applicable", description: "Relevant to priority topics and populations" },
  { label: "Valid", description: "Accurately reflects the concept being measured" },
  { label: "Actionable", description: "Able to signal progress, where progress needs to be made, and where there is NO progress." },
  { label: "Feasible", description: "Practical to collect, aggregate, and share" },
  { label: "Understandable", description: "Clear for audience interpretation" },
  { label: "Timely", description: "Updated often enough to support improvement" },
  { label: "Comparative", description: "Consistent across time" },
  { label: "Reliable", description: "Well-defined and consistently used" },
  { label: "Sustainable", description: "Likely to continue to be collected over time" },
  { label: "Representative", description: "Reflective of the full population" },
  { label: "Contextually-supported", description: "Able to reflect the context" },
];

const SELECTED_MEASURE_GROUPS = [
  { id: "care", title: "Care Practices", metricCategory: "Care Practices" },
  { id: "context", title: "Context, Competency, and Tracking", metricCategory: "Context & Tracking" },
  { id: "coverage", title: "Coverage and Reach", metricCategory: "Coverage & Reach" },
  { id: "impact", title: "Achieving Impact", metricCategory: "Impact Data" },
].map((group) => ({
  ...group,
  metrics: OVERVIEW_METRICS
    .filter((metric) => metric.eyebrow === group.metricCategory)
    .map((metric) => ({ ...metric, frameworkId: group.id, frameworkTitle: group.title })),
}));

const SELECTED_MEASURE_COLUMNS = SELECTED_MEASURE_GROUPS.map((group) => ({
  id: group.id,
  frameworkId: group.id,
  title: group.title,
  metrics: group.metrics,
}));

const DEFAULT_YEARS = Array.from({ length: 10 }, (_, index) => 2016 + index);
const MAX_SELECTED_STATES = 2;

const MAP_LAYERS = [
  {
    id: "crisis988",
    label: "988 Calls",
    shortLabel: "988",
    metric: "988 Lifeline KPI data",
    mapTitle: "United States 988 KPI map",
    mapSubtitle: "Monthly 988 Lifeline KPI data from processed state KPI files",
    trendTitle: "988 KPI trend",
    valueLabel: "KPI value",
    summaryPrefix: "KPI",
    zmin: 0,
    zmax: 100,
    colorscale: [
      [0, "#f7fcf5"],
      [0.35, "#c7e9c0"],
      [0.62, "#74c476"],
      [0.82, "#238b45"],
      [1, "#00441b"],
    ],
  },
  {
    id: "suicideAdjustedRate",
    label: "Age-Adjusted Suicide Rate",
    shortLabel: "Rate",
    metric: "CDC age-adjusted suicide rate per 100,000 residents",
    mapTitle: "United States age-adjusted suicide rate map",
    mapSubtitle: "CDC age-adjusted suicide rate from processed outcomes data",
    trendTitle: "Age-adjusted suicide rate",
    valueLabel: "Rate per 100,000",
    summaryPrefix: "rate",
    zmin: 7,
    zmax: 31,
    colorscale: [
      [0, "#fff7ec"],
      [0.35, "#fdd49e"],
      [0.62, "#f16913"],
      [0.82, "#d94801"],
      [1, "#7f2704"],
    ],
  },
];
const MAP_LAYER_BY_ID = Object.fromEntries(MAP_LAYERS.map((layer) => [layer.id, layer]));
const DEFAULT_LAYER_ID = "suicideAdjustedRate";
const OUTCOME_LAYER_TO_API_LAYER = {
  suicideAdjustedRate: "adjusted",
};
const DEFAULT_OUTCOME_FILTERS = {
  breakdownType: "overall",
  gender: "All",
  race: "All",
  cause: "All",
  education: "All",
  placeOfDeath: "All",
};
const DEFAULT_988_METRIC = "in_state_answer_rate";
const KPI_988_OPTIONS = [
  { metric: "in_state_answer_rate", label: "In-State Answer Rate", group: "answer_rate", valueType: "percent", yAxisLabel: "Percent" },
  { metric: "asa_in_state_seconds", label: "Avg Speed to Answer", group: "time", valueType: "time", yAxisLabel: "Time" },
  { metric: "avg_talk_time_in_state_seconds", label: "Avg Talk Time", group: "time", valueType: "time", yAxisLabel: "Time" },
];
const KPI_988_BY_METRIC = Object.fromEntries(KPI_988_OPTIONS.map((option) => [option.metric, option]));
const LAST_RETRIEVED_LABEL = "Last retrieved: June 17, 2026";
const DATA_SOURCE_META = {
  suicide: {
    label: LAST_RETRIEVED_LABEL,
    sourceName: "CDC WONDER",
    sourceUrl: "https://wonder.cdc.gov/",
  },
  crisis988: {
    label: LAST_RETRIEVED_LABEL,
    sourceName: "988 Lifeline",
    sourceUrl: "https://988lifeline.org/professionals/our-network/state-based-monthly-reports/",
  },
};
const INDICATOR_LAYER_IDS = MAP_LAYERS.filter((layer) => !OUTCOME_LAYER_TO_API_LAYER[layer.id]).map((layer) => layer.id);
const OUTCOME_LAYER_IDS = MAP_LAYERS.filter((layer) => OUTCOME_LAYER_TO_API_LAYER[layer.id]).map((layer) => layer.id);
const DEFAULT_INDICATOR_LAYER_ID = INDICATOR_LAYER_IDS[0] || DEFAULT_LAYER_ID;
const DATA_SELECTION_GROUPS = [
  {
    label: "Context & Tracking",
    options: KPI_988_OPTIONS.map((option) => ({
      value: `kpi:${option.metric}`,
      label: option.label,
    })),
  },
  {
    label: "Impact",
    options: OUTCOME_LAYER_IDS.map((layerId) => {
      const layer = MAP_LAYER_BY_ID[layerId];
      return layer ? { value: `layer:${layer.id}`, label: layer.label } : null;
    }).filter(Boolean),
  },
  {
    label: "Coverage & Reach",
    options: [{
      value: NSDUH_TREATMENT_SERIES.selectionValue,
      label: NSDUH_TREATMENT_SERIES.label,
    }],
  },
];
const BREAKDOWN_FILTER_FIELDS = {
  overall: [],
  gender: ["gender"],
  race: ["race"],
  cause: ["cause"],
  education: ["education"],
  place_of_death: ["placeOfDeath"],
};
const EXPLORE_BREAKDOWN_TYPES = [
  "overall",
  "gender",
  "race",
  "cause",
  "education",
  "place_of_death",
];
const BREAKDOWN_LABELS = {
  overall: "Overall",
  gender: "Sex",
  race: "Race",
  cause: "Method",
  education: "Education",
  place_of_death: "Place of Death",
};
const OUTCOME_FILTER_FIELDS = [
  { key: "gender", label: "Sex", optionKey: "gender" },
  { key: "race", label: "Race", optionKey: "race" },
  { key: "cause", label: "Method", optionKey: "cause" },
  { key: "education", label: "Education", optionKey: "education" },
  { key: "placeOfDeath", label: "Place of death", optionKey: "place_of_death" },
];
const MAX_DASHBOARD_CATEGORY_SELECTIONS = 3;

function emptyDashboardCategorySelections() {
  return OUTCOME_FILTER_FIELDS.reduce((selections, field) => {
    selections[field.key] = [];
    return selections;
  }, {});
}

function normalizeDashboardCategorySelections(values, options, limit = MAX_DASHBOARD_CATEGORY_SELECTIONS) {
  const allowed = new Set(Array.isArray(options) ? options : []);
  const selected = [];
  (Array.isArray(values) ? values : []).forEach((value) => {
    if (!allowed.has(value) || selected.includes(value) || selected.length >= limit) return;
    selected.push(value);
  });
  return selected;
}

function sameStringArray(left, right) {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}
const STORY_CARDS = [
  {
    title: "A reason to reach out",
    imageUrl: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Hope after a hard season",
    imageUrl: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Someone listened",
    imageUrl: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Small steps back to care",
    imageUrl: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Finding steady ground",
    imageUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Connection changed the day",
    imageUrl: "https://images.unsplash.com/photo-1544723795-3fb6469f5b39?auto=format&fit=crop&w=900&q=80",
  },
];


const STATE_RESOURCE_LINKS = {
  colorado: {
    label: "Colorado",
    resources: [
      {
        label: "Colorado Violent Death Reporting System Suicide Dashboard",
        url: "https://cohealthviz.dphe.state.co.us/t/HealthInformaticsPublic/views/COVDRSSuicideDashboardAllYearsExcludesRace/Story1?%3Aembed=y&%3AisGuestRedirectFromVizportal=y",
      },
    ],
  },
  arizona: {
    label: "Arizona",
    resources: [
      {
        label: "Arizona Department of Health Services Suicide Prevention Data and Reports",
        url: "https://www.azdhs.gov/prevention/chronic-disease/suicide-prevention/index.php#data-and-reports",
      },
    ],
  },
};

const FEDERAL_RESOURCE_GROUPS = [
  {
    category: "Strategy and planning",
    links: [
      {
        description: "Goals and objectives framework for mapping metrics.",
        url: "https://www.hhs.gov/sites/default/files/national-strategy-suicide-prevention.pdf",
      },
      {
        description: "Near-term federal actions that inform indicator priorities.",
        url: "https://www.hhs.gov/sites/default/files/nnsp-federal-action-plan.pdf",
      },
    ],
  },
  {
    category: "Mortality and injury data",
    links: [
      {
        description: "State map/table for suicide mortality rates.",
        url: "https://www.cdc.gov/nchs/state-stats/deaths/suicide.html",
      },
      {
        description: "Fatal and nonfatal injury data query tool.",
        url: "https://wisqars.cdc.gov/",
      },
    ],
  },
  {
    category: "Mental health and population indicators",
    links: [
      {
        description: "Youth mental health and suicide risk indicators across national and state/local results.",
        url: "https://www.cdc.gov/yrbs/results/2023-yrbs-results.html",
      },
      {
        description: "State mental health, substance use disorder, and suicidal thoughts estimates for 2021-2023 pooled data.",
        url: "https://www.samhsa.gov/data/report/nsduh-behavioral-health-barometers-state-volume-8",
      },
      {
        description: "Interactive state estimates for mental health and substance use disorder indicators.",
        url: "https://nsduhweb.rti.org/respweb/estimates.html",
      },
    ],
  },
  {
    category: "Treatment, crisis response, and workforce",
    links: [
      {
        description: "State treatment facility characteristics, services, client counts, and maps.",
        url: "https://www.samhsa.gov/data/report/2024-n-sumhss-state-profiles",
      },
      {
        description: "Monthly 988 network performance metrics dashboard.",
        url: "https://www.samhsa.gov/mental-health/988/performance-metrics",
      },
      {
        description: "State-by-state in-state answer rates, speed to answer, and related 988 network metrics.",
        url: "https://988lifeline.org/professionals/our-network/network-metrics/",
      },
      {
        description: "Official mental health shortage area designations and exports.",
        url: "https://data.hrsa.gov/topics/health-workforce/shortage-areas/hpsa-find",
      },
      {
        description: "County, state, and national workforce plus system indicators from more than 50 sources.",
        url: "https://data.hrsa.gov/topics/health-workforce/ahrf",
      },
    ],
  },
  {
    category: "Follow-up quality measures",
    links: [
      {
        description: "Follow-up after hospitalization for mental illness measure description.",
        url: "https://www.ncqa.org/report-cards/health-plans/state-of-health-care-quality-report/follow-up-after-hospitalization-for-mental-illness-fuh/",
      },
      {
        description: "Follow-up after emergency department visit for mental illness measure description.",
        url: "https://www.ncqa.org/report-cards/health-plans/state-of-health-care-quality-report/follow-up-emergency-department-visit-for-mental-illness-fum/",
      },
    ],
  },
];

const STATES = [
  { code: "AL", name: "Alabama", base: 15.4, trend: 0.31, volatility: 0.6 },
  { code: "AK", name: "Alaska", base: 25.8, trend: 0.42, volatility: 1.2 },
  { code: "AZ", name: "Arizona", base: 18.2, trend: 0.24, volatility: 0.8 },
  { code: "AR", name: "Arkansas", base: 17.2, trend: 0.32, volatility: 0.7 },
  { code: "CA", name: "California", base: 10.4, trend: 0.08, volatility: 0.4 },
  { code: "CO", name: "Colorado", base: 20.8, trend: 0.28, volatility: 0.8 },
  { code: "CT", name: "Connecticut", base: 10.1, trend: 0.07, volatility: 0.3 },
  { code: "DE", name: "Delaware", base: 12.6, trend: 0.15, volatility: 0.4 },
  { code: "FL", name: "Florida", base: 13.8, trend: 0.16, volatility: 0.5 },
  { code: "GA", name: "Georgia", base: 13.3, trend: 0.17, volatility: 0.5 },
  { code: "HI", name: "Hawaii", base: 12.7, trend: 0.1, volatility: 0.4 },
  { code: "ID", name: "Idaho", base: 21.4, trend: 0.34, volatility: 0.8 },
  { code: "IL", name: "Illinois", base: 10.8, trend: 0.11, volatility: 0.4 },
  { code: "IN", name: "Indiana", base: 15.2, trend: 0.24, volatility: 0.6 },
  { code: "IA", name: "Iowa", base: 13.9, trend: 0.2, volatility: 0.5 },
  { code: "KS", name: "Kansas", base: 16.1, trend: 0.27, volatility: 0.6 },
  { code: "KY", name: "Kentucky", base: 17.6, trend: 0.29, volatility: 0.7 },
  { code: "LA", name: "Louisiana", base: 14.5, trend: 0.21, volatility: 0.6 },
  { code: "ME", name: "Maine", base: 18.4, trend: 0.2, volatility: 0.6 },
  { code: "MD", name: "Maryland", base: 10.2, trend: 0.08, volatility: 0.3 },
  { code: "MA", name: "Massachusetts", base: 9.4, trend: 0.06, volatility: 0.3 },
  { code: "MI", name: "Michigan", base: 13.8, trend: 0.18, volatility: 0.5 },
  { code: "MN", name: "Minnesota", base: 12.9, trend: 0.16, volatility: 0.4 },
  { code: "MS", name: "Mississippi", base: 14.9, trend: 0.26, volatility: 0.6 },
  { code: "MO", name: "Missouri", base: 17.8, trend: 0.31, volatility: 0.7 },
  { code: "MT", name: "Montana", base: 27.2, trend: 0.48, volatility: 1.1 },
  { code: "NE", name: "Nebraska", base: 13.4, trend: 0.18, volatility: 0.5 },
  { code: "NV", name: "Nevada", base: 20.5, trend: 0.18, volatility: 0.8 },
  { code: "NH", name: "New Hampshire", base: 16.7, trend: 0.19, volatility: 0.5 },
  { code: "NJ", name: "New Jersey", base: 8.3, trend: 0.05, volatility: 0.3 },
  { code: "NM", name: "New Mexico", base: 23.5, trend: 0.38, volatility: 0.9 },
  { code: "NY", name: "New York", base: 8.6, trend: 0.04, volatility: 0.3 },
  { code: "NC", name: "North Carolina", base: 13.7, trend: 0.19, volatility: 0.5 },
  { code: "ND", name: "North Dakota", base: 19.4, trend: 0.36, volatility: 0.8 },
  { code: "OH", name: "Ohio", base: 14.8, trend: 0.23, volatility: 0.6 },
  { code: "OK", name: "Oklahoma", base: 19.3, trend: 0.34, volatility: 0.8 },
  { code: "OR", name: "Oregon", base: 17.6, trend: 0.19, volatility: 0.6 },
  { code: "PA", name: "Pennsylvania", base: 13.1, trend: 0.16, volatility: 0.5 },
  { code: "RI", name: "Rhode Island", base: 10.0, trend: 0.06, volatility: 0.3 },
  { code: "SC", name: "South Carolina", base: 15.0, trend: 0.22, volatility: 0.6 },
  { code: "SD", name: "South Dakota", base: 18.8, trend: 0.35, volatility: 0.8 },
  { code: "TN", name: "Tennessee", base: 16.4, trend: 0.27, volatility: 0.7 },
  { code: "TX", name: "Texas", base: 12.7, trend: 0.17, volatility: 0.5 },
  { code: "UT", name: "Utah", base: 20.3, trend: 0.32, volatility: 0.8 },
  { code: "VT", name: "Vermont", base: 16.0, trend: 0.16, volatility: 0.5 },
  { code: "VA", name: "Virginia", base: 12.4, trend: 0.14, volatility: 0.4 },
  { code: "WA", name: "Washington", base: 14.9, trend: 0.17, volatility: 0.5 },
  { code: "WV", name: "West Virginia", base: 19.8, trend: 0.36, volatility: 0.8 },
  { code: "WI", name: "Wisconsin", base: 14.6, trend: 0.21, volatility: 0.5 },
  { code: "WY", name: "Wyoming", base: 27.9, trend: 0.44, volatility: 1.1 },
];

const STATE_BY_CODE = Object.fromEntries(STATES.map((state) => [state.code, state]));
const STATE_COLORS = ["#0072B2", "#009E73", "#CC79A7", "#F0E442", "#56B4E9"];
const TREND_LINE_COLORS = ["#0072B2", "#009E73", "#CC79A7", "#D55E00", "#5d8ba0", "#6b5fb5", "#8b5e34", "#4b5563", "#B56576"];

function isFiniteNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
}

function isOutcomeLayer(layerOrId) {
  const layerId = typeof layerOrId === "string" ? layerOrId : layerOrId?.id;
  return Boolean(OUTCOME_LAYER_TO_API_LAYER[layerId]);
}

function is988Layer(layerOrId) {
  const layerId = typeof layerOrId === "string" ? layerOrId : layerOrId?.id;
  return layerId === "crisis988";
}

function outcomeApiLayerFor(layerOrId) {
  const layerId = typeof layerOrId === "string" ? layerOrId : layerOrId?.id;
  return OUTCOME_LAYER_TO_API_LAYER[layerId] || "adjusted";
}

function optionValues(layerOptions, optionKey) {
  const values = layerOptions?.[optionKey];
  return Array.isArray(values) ? values : [];
}

function categoryFieldForBreakdown(breakdownType) {
  return OUTCOME_FILTER_FIELDS.find((field) => (BREAKDOWN_FILTER_FIELDS[breakdownType] || []).includes(field.key)) || null;
}

function selectableCategoryValues(layerOptions, breakdownType) {
  const field = categoryFieldForBreakdown(breakdownType);
  if (!field) return [];
  return optionValues(layerOptions, field.optionKey).filter((value) => value && value !== "All");
}

function defaultCategoryForBreakdown(layerOptions, breakdownType) {
  const values = selectableCategoryValues(layerOptions, breakdownType);
  if (breakdownType === "gender" && values.includes("Male")) return "Male";
  return values[0] || "All";
}

function sanitizeOutcomeFilters(filters, layerOptions = {}) {
  const breakdownTypes = optionValues(layerOptions, "breakdownTypes");
  const breakdownType = breakdownTypes.includes(filters.breakdownType)
    ? filters.breakdownType
    : breakdownTypes.includes("overall")
      ? "overall"
      : breakdownTypes[0] || "overall";
  const activeFields = BREAKDOWN_FILTER_FIELDS[breakdownType] || [];

  return OUTCOME_FILTER_FIELDS.reduce((normalized, field) => {
    const values = optionValues(layerOptions, field.optionKey);
    const requested = filters[field.key] || "All";
    normalized[field.key] = activeFields.includes(field.key) && values.includes(requested) ? requested : "All";
    return normalized;
  }, { breakdownType });
}

function yearsFromRows(rows) {
  const yearSet = new Set();
  rows.forEach((row) => {
    (row.values || []).forEach((point) => {
      if (isFiniteNumber(point.year)) yearSet.add(Number(point.year));
    });
  });
  return Array.from(yearSet).sort((a, b) => a - b);
}

function yearsFromOutcomeRows(outcomeRows) {
  return yearsFromRows(outcomeRows);
}

function yearsFrom988Rows(crisis988Rows) {
  return yearsFromRows(crisis988Rows);
}

function outcomeRowsByCode(outcomeRows) {
  const byCode = new Map();
  outcomeRows.forEach((row) => {
    const valuesByYear = new Map();
    (row.values || []).forEach((point) => {
      if (isFiniteNumber(point.year)) valuesByYear.set(Number(point.year), point);
    });
    if (row.code) byCode.set(row.code, valuesByYear);
  });
  return byCode;
}

function buildData(outcomeRows = [], years = DEFAULT_YEARS, crisis988Rows = []) {
  const outcomesByCode = outcomeRowsByCode(outcomeRows);
  const crisis988ByCode = new Map();
  crisis988Rows.forEach((row) => {
    const valuesByYear = new Map();
    (row.values || []).forEach((point) => {
      if (isFiniteNumber(point.year)) valuesByYear.set(Number(point.year), point);
    });
    if (row.code) crisis988ByCode.set(row.code, valuesByYear);
  });
  return STATES.map((state) => ({
    ...state,
    values: years.map((year) => {
      const selectedYear = Number(year);
      const outcome = outcomesByCode.get(state.code)?.get(selectedYear) || {};
      const crisis988Point = crisis988ByCode.get(state.code)?.get(selectedYear) || {};
      return {
        year,
        crisis988: crisis988Point.crisis988 ?? null,
        crisis988Date: crisis988Point.date || null,
        crisis988Year: crisis988Point.year ?? null,
        crisis988Month: crisis988Point.month ?? null,
        crisis988SourceGranularity: crisis988Point.source_granularity || null,
        suicideCount: outcome.suicideCount ?? null,
        suicideAdjustedRate: outcome.suicideAdjustedRate ?? null,
      };
    }),
  }));
}

function layerValueForYear(row, layerId, year) {
  const layerKey = MAP_LAYER_BY_ID[layerId] ? layerId : DEFAULT_LAYER_ID;
  const selectedYear = Number(year);
  const point = (row.values || []).find((value) => Number(value.year) === selectedYear);
  return point?.[layerKey] ?? null;
}

function latestLayerValue(row, layerId) {
  const layerKey = MAP_LAYER_BY_ID[layerId] ? layerId : DEFAULT_LAYER_ID;
  const values = row.values || [];
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index]?.[layerKey];
    if (isFiniteNumber(value)) return value;
  }
  return null;
}

function tenYearLayerChange(row, layerId) {
  const layerKey = MAP_LAYER_BY_ID[layerId] ? layerId : DEFAULT_LAYER_ID;
  const values = (row.values || []).filter((point) => isFiniteNumber(point[layerKey]));
  const firstValue = values[0]?.[layerKey];
  const latestValue = values[values.length - 1]?.[layerKey];
  if (!isFiniteNumber(firstValue) || !isFiniteNumber(latestValue)) return null;
  return Number((Number(latestValue) - Number(firstValue)).toFixed(1));
}

function yearsFromTrendSeries(series) {
  const yearSet = new Set();
  (series || []).forEach((item) => {
    (item.values || []).forEach((point) => {
      if (isFiniteNumber(point.year)) yearSet.add(Number(point.year));
    });
  });
  return Array.from(yearSet).sort((a, b) => a - b);
}


function valueByYear(values = []) {
  return new Map(values.map((point) => [Number(point.year), point.value]));
}

function formatTrendHoverValue(value, formatter = null) {
  if (!isFiniteNumber(value)) return "No data";
  if (formatter) return formatter(value);
  return Number(value).toFixed(1);
}

function formatSeconds(value) {
  if (!isFiniteNumber(value)) return "No data";
  const totalSeconds = Math.round(Number(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

function format988Value(value, metric) {
  const option = KPI_988_BY_METRIC[metric] || KPI_988_BY_METRIC[DEFAULT_988_METRIC];
  if (!isFiniteNumber(value)) return "No data";
  if (option.valueType === "percent") return `${Number(value).toFixed(1)}%`;
  if (option.valueType === "time") return formatSeconds(value);
  return Number(value).toFixed(1);
}

function formatLayerValue(value, layer = null, metric = DEFAULT_988_METRIC) {
  if (!isFiniteNumber(value)) return "No data";
  if (is988Layer(layer)) return format988Value(value, metric);
  if (layer?.valueFormat === "percent") return `${Number(value).toFixed(1)}%`;
  if (layer?.valueFormat === "integer") return Math.round(Number(value)).toLocaleString();
  return Number(value).toFixed(1);
}

const DISCRETE_MAP_TOTAL_BINS = 5;
const DISCRETE_MAP_VALUE_BINS = DISCRETE_MAP_TOTAL_BINS - 1;
const MAP_NO_DATA_COLOR = "#d7dde2";

function steppedColorscale(colors) {
  const step = 1 / colors.length;
  return colors.flatMap((color, index) => {
    const start = Number((index * step).toFixed(4));
    const end = Number(((index + 1) * step).toFixed(4));
    return [[start, color], [end, color]];
  });
}

function valueBinColorsForLayer(layer, metric = null) {
  if (is988Layer(layer) && metric === "asa_in_state_seconds") {
    return ["#1a9850", "#8ccf67", "#f2c85b", "#c97845"];
  }

  const colors = (layer?.colorscale || []).map((entry) => entry[1]).filter(Boolean);
  const selected = colors.length >= 5
    ? [colors[0], colors[1], colors[2], colors[4]]
    : colors.slice(0, DISCRETE_MAP_VALUE_BINS);
  while (selected.length < DISCRETE_MAP_VALUE_BINS) {
    selected.push(selected[selected.length - 1] || "#5d8ba0");
  }
  return selected;
}

function discreteMapScaleForLayer(layer, values, metric, rangeMax) {
  const numericValues = values.filter(isFiniteNumber).map(Number);
  const fallbackMin = numericValues.length ? Math.min(...numericValues) : 0;
  const fallbackMax = numericValues.length ? Math.max(...numericValues) : fallbackMin + DISCRETE_MAP_VALUE_BINS;
  const rangeMin = isFiniteNumber(layer?.zmin) ? Number(layer.zmin) : fallbackMin;
  const candidateMax = isFiniteNumber(rangeMax)
    ? Number(rangeMax)
    : isFiniteNumber(layer?.zmax)
      ? Number(layer.zmax)
      : fallbackMax;
  const rangeUpper = candidateMax > rangeMin ? candidateMax : rangeMin + DISCRETE_MAP_VALUE_BINS;
  const step = (rangeUpper - rangeMin) / DISCRETE_MAP_VALUE_BINS;
  const thresholds = Array.from({ length: DISCRETE_MAP_VALUE_BINS }, (_, index) => rangeMin + step * (index + 1));
  const formatBoundary = (value) => formatLayerValue(value, layer, metric);
  const ticktext = [
    "No data",
    `< ${formatBoundary(thresholds[0])}`,
    `${formatBoundary(thresholds[0])} - < ${formatBoundary(thresholds[1])}`,
    `${formatBoundary(thresholds[1])} - < ${formatBoundary(thresholds[2])}`,
    `>= ${formatBoundary(thresholds[2])}`,
  ];
  const tickvals = Array.from({ length: DISCRETE_MAP_TOTAL_BINS }, (_, index) => index + 0.5);
  const colors = [MAP_NO_DATA_COLOR, ...valueBinColorsForLayer(layer, metric)];
  const binIndexForValue = (value) => {
    if (!isFiniteNumber(value)) return 0;
    const numericValue = Number(value);
    if (numericValue < thresholds[0]) return 1;
    if (numericValue < thresholds[1]) return 2;
    if (numericValue < thresholds[2]) return 3;
    return 4;
  };

  return {
    colorscale: steppedColorscale(colors),
    zmin: 0,
    zmax: DISCRETE_MAP_TOTAL_BINS,
    tickvals,
    ticktext,
    binValue: (value) => binIndexForValue(value) + 0.5,
    binLabel: (value) => ticktext[binIndexForValue(value)],
  };
}

function MetricSelectionFrameworkIcon({ type }) {
  const iconProps = {
    className: "metric-selection-framework-icon",
    viewBox: "0 0 72 72",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.3,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    focusable: "false",
  };

  if (type === "care") {
    return (
      <svg {...iconProps}>
        <path d="M18 14v12c0 10.4 7.9 18 18 18s18-7.6 18-18V14" />
        <path d="M15 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Zm36 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z" />
        <path d="M36 44v10c0 6.6 5.4 12 12 12h4" />
        <circle cx="58" cy="60" r="6" />
        <path d="M58 54v-7" />
      </svg>
    );
  }

  if (type === "context") {
    return (
      <svg {...iconProps}>
        <path d="M14 58V14M14 58h46" />
        <path d="M17 35c5-18 11-21 16-20 8 2 7 23 16 23 4 0 7-4 10-10" />
        <path d="m54 28 5 0-2.5 5" />
      </svg>
    );
  }

  return (
    <svg {...iconProps}>
      <circle cx="36" cy="36" r="25" />
      <path d="M11 36h50M36 11v50" />
      <path d="M36 11c8 7 12 15 12 25s-4 18-12 25M36 11c-8 7-12 15-12 25s4 18 12 25" />
    </svg>
  );
}

function SourceMetadata({ meta }) {
  if (!meta) return null;
  const links = Array.isArray(meta.links) && meta.links.length
    ? meta.links
    : meta.sourceUrl
      ? [{ label: meta.linkLabel || "Source", url: meta.sourceUrl }]
      : [];
  return (
    <span className="data-source-meta">
      {meta.label ? <span>{meta.label}</span> : null}
      {links.map((link) => (
        <a className="source-link-button" href={link.url} target="_blank" rel="noreferrer" key={link.url}>{link.label}</a>
      ))}
    </span>
  );
}

function getBreakdownPanelTitle(layer, breakdownType) {
  const breakdownLabel = BREAKDOWN_LABELS[breakdownType] || breakdownType?.replaceAll("_", " ") || "Breakdown";
  return `${layer?.label || "Outcome"} — ${breakdownLabel} Breakdown`;
}

function getSessionFlag(key) {
  try {
    return typeof window !== "undefined" && window.sessionStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function setSessionFlag(key) {
  try {
    window.sessionStorage.setItem(key, "true");
  } catch {
    // Ignore storage failures; scrolling should still be best-effort.
  }
}

function getPersistentFlag(key) {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function setPersistentFlag(key) {
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // Ignore storage failures; the tour can still be dismissed for this render.
  }
}


function BreakdownComparisonChart({ data, layer, breakdownType, loading, error }) {
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const categories = Array.from(new Set(groups.flatMap((group) => (group.values || []).map((item) => item.category))));
  const horizontal = ["race", "cause", "education", "place_of_death"].includes(breakdownType);

  if (loading) return <div className="breakdown-chart-status" role="status">Loading breakdown comparison...</div>;
  if (error) return <div className="breakdown-chart-status error" role="alert">{error}</div>;
  if (!groups.length || !categories.length) {
    return <div className="breakdown-chart-status">{data?.message || "No breakdown data available for the selected states."}</div>;
  }

  const traces = groups.map((group, index) => {
    const valueByCategory = new Map((group.values || []).map((item) => [item.category, item.value]));
    const values = categories.map((category) => valueByCategory.get(category) ?? null);
    const stateColorIndex = groups.slice(0, index).filter((item) => item.type === "state").length;
    return {
      type: "bar",
      orientation: horizontal ? "h" : "v",
      name: group.label,
      x: horizontal ? values : categories,
      y: horizontal ? categories : values,
      marker: { color: group.type === "national" ? "#5d8ba0" : STATE_COLORS[stateColorIndex % STATE_COLORS.length] },
      hovertemplate: horizontal
        ? group.label + "<br>%{y}: %{x:.1f}<extra></extra>"
        : group.label + "<br>%{x}: %{y:.1f}<extra></extra>",
    };
  });

  return (
    <div className={"comparison-plot-frame " + (breakdownType === "cause" ? "comparison-plot-frame-full" : "")}>
      <Plot
        data={traces}
        layout={{
          barmode: "group",
          bargap: horizontal ? 0.38 : 0.34,
          bargroupgap: 0.12,
          margin: horizontal ? { l: 132, r: 10, t: 18, b: 42 } : { l: 54, r: 10, t: 18, b: 86 },
          paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(255,255,255,0.5)",
        xaxis: horizontal
          ? { title: layer.valueLabel, rangemode: "tozero", gridcolor: "rgba(93,139,160,0.16)" }
          : { tickangle: -28, automargin: true, gridcolor: "rgba(93,139,160,0.12)" },
        yaxis: horizontal
          ? { automargin: true, gridcolor: "rgba(93,139,160,0.12)" }
          : { title: layer.valueLabel, rangemode: "tozero", gridcolor: "rgba(93,139,160,0.16)" },
        showlegend: true,
        legend: { orientation: "h", y: -0.24, x: 0 },
        hovermode: "closest",
      }}
        config={{ displayModeBar: false, responsive: true }}
        useResizeHandler
        className="breakdown-comparison-plot"
      />
    </div>
  );
}

function Kpi988ComparisonChart({ data, metric, loading, error, includeNational, selectedCount }) {
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const categories = Array.from(new Set(groups.flatMap((group) => (group.values || []).map((item) => item.category))));
  const metricOption = KPI_988_BY_METRIC[metric] || KPI_988_BY_METRIC[DEFAULT_988_METRIC];

  if (loading) return <div className="breakdown-chart-status" role="status">Loading 988 KPI comparison...</div>;
  if (error) return <div className="breakdown-chart-status error" role="alert">{error}</div>;
  if (!includeNational && !selectedCount) {
    return <div className="breakdown-chart-status">Use the Location dropdown to select up to 2 states, or turn on national comparison to view 988 KPI data.</div>;
  }
  if (!groups.length || !categories.length) {
    return <div className="breakdown-chart-status">{data?.message || "No 988 KPI data available for this selection."}</div>;
  }

  const traces = groups.map((group, index) => {
    const valueByCategory = new Map((group.values || []).map((item) => [item.category, item.value]));
    const metricByCategory = new Map((group.values || []).map((item) => [item.category, item.metric]));
    const values = categories.map((category) => valueByCategory.get(category) ?? null);
    const customdata = categories.map((category) => format988Value(valueByCategory.get(category), metricByCategory.get(category)));
    const stateColorIndex = groups.slice(0, index).filter((item) => item.type === "state").length;
    return {
      type: "bar",
      name: group.label,
      x: categories,
      y: values,
      customdata,
      marker: { color: group.type === "national" ? "#5d8ba0" : STATE_COLORS[stateColorIndex % STATE_COLORS.length] },
      hovertemplate: group.label + "<br>%{x}: %{customdata}<extra></extra>",
    };
  });

  return (
    <div className="comparison-plot-frame comparison-plot-frame-kpi">
      <Plot
        data={traces}
        layout={{
          barmode: "group",
          bargap: 0.44,
          bargroupgap: 0.14,
          margin: { l: 58, r: 10, t: 18, b: 70 },
          paper_bgcolor: "rgba(0,0,0,0)",
          plot_bgcolor: "rgba(255,255,255,0.5)",
          xaxis: { tickangle: -16, automargin: true, gridcolor: "rgba(93,139,160,0.12)" },
          yaxis: {
            title: metricOption?.yAxisLabel || "KPI value",
            ticksuffix: metricOption?.valueType === "time" ? " sec" : undefined,
            rangemode: "tozero",
            gridcolor: "rgba(93,139,160,0.16)",
          },
          legend: { orientation: "h", y: -0.24, x: 0 },
          hovermode: "closest",
        }}
        config={{ displayModeBar: false, responsive: true }}
        useResizeHandler
        className="breakdown-comparison-plot"
      />
    </div>
  );
}


function Dashboard({ currentUser, onLogout, onLoginClick }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [dataDashboardView, setDataDashboardView] = useState("overview");
  const [overviewMetricMessage, setOverviewMetricMessage] = useState("");
  const [dashboardInfoMessage, setDashboardInfoMessage] = useState("");
  const [mapYear, setMapYear] = useState(DEFAULT_YEARS[DEFAULT_YEARS.length - 1]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [stateSelectionMessage, setStateSelectionMessage] = useState("");
  const [exploreLayerId, setExploreLayerId] = useState(DEFAULT_LAYER_ID);
  const [nationalMetricId, setNationalMetricId] = useState(null);
  const [nsduhTreatmentData, setNsduhTreatmentData] = useState(null);
  const [nsduhTreatmentLoading, setNsduhTreatmentLoading] = useState(false);
  const [nsduhTreatmentError, setNsduhTreatmentError] = useState("");
  const [outcomeRows, setOutcomeRows] = useState([]);
  const [crisis988Rows, setCrisis988Rows] = useState([]);
  const [explore988Metric, setExplore988Metric] = useState(DEFAULT_988_METRIC);
  const [crisis988Loading, setCrisis988Loading] = useState(false);
  const [crisis988Error, setCrisis988Error] = useState("");
  const [crisis988ComparisonData, setCrisis988ComparisonData] = useState(null);
  const [crisis988ComparisonLoading, setCrisis988ComparisonLoading] = useState(false);
  const [crisis988ComparisonError, setCrisis988ComparisonError] = useState("");
  const [showNational988Trend, setShowNational988Trend] = useState(true);
  const [align988TrendYearly, setAlign988TrendYearly] = useState(true);
  const [hoveredInsightYear, setHoveredInsightYear] = useState(null);
  const [crisis988TrendData, setCrisis988TrendData] = useState(null);
  const [crisis988TrendLoading, setCrisis988TrendLoading] = useState(false);
  const [crisis988TrendError, setCrisis988TrendError] = useState("");
  const [outcomeOptions, setOutcomeOptions] = useState({});
  const [breakdownType, setBreakdownType] = useState(DEFAULT_OUTCOME_FILTERS.breakdownType);
  const [gender, setGender] = useState(DEFAULT_OUTCOME_FILTERS.gender);
  const [race, setRace] = useState(DEFAULT_OUTCOME_FILTERS.race);
  const [cause, setCause] = useState(DEFAULT_OUTCOME_FILTERS.cause);
  const [education, setEducation] = useState(DEFAULT_OUTCOME_FILTERS.education);
  const [placeOfDeath, setPlaceOfDeath] = useState(DEFAULT_OUTCOME_FILTERS.placeOfDeath);
  const [dashboardCategorySelections, setDashboardCategorySelections] = useState(() => emptyDashboardCategorySelections());
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [breakdownSelectionExpanded, setBreakdownSelectionExpanded] = useState(false);
  const [showExploreNationalComparison, setShowExploreNationalComparison] = useState(true);
  const [showNationalTrend, setShowNationalTrend] = useState(true);
  const [outcomeTrendData, setOutcomeTrendData] = useState(null);
  const [outcomeTrendLoading, setOutcomeTrendLoading] = useState(false);
  const [outcomeTrendError, setOutcomeTrendError] = useState("");
  const [overviewNationalOutcomeData, setOverviewNationalOutcomeData] = useState(null);
  const [overviewNationalOutcomeLoading, setOverviewNationalOutcomeLoading] = useState(false);
  const [overviewNationalOutcomeError, setOverviewNationalOutcomeError] = useState("");
  const [outcomeBreakdownData, setOutcomeBreakdownData] = useState(null);
  const [outcomeBreakdownLoading, setOutcomeBreakdownLoading] = useState(false);
  const [outcomeBreakdownError, setOutcomeBreakdownError] = useState("");
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [outcomeOptionsLoading, setOutcomeOptionsLoading] = useState(false);
  const [outcomeError, setOutcomeError] = useState("");
  const breakdownPanelRef = useRef(null);
  const kpiComparisonPanelRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const dashboardCategorySelectionTouchedRef = useRef(false);
  const overviewRevealRef = useRef(null);
  const overviewMetricMessageTimerRef = useRef(null);
  const dashboardInfoMessageTimerRef = useRef(null);
  const hasScrolledToBreakdownRef = useRef(getSessionFlag("explore_breakdown_panel_seen"));
  const previousExploreYearDomainRef = useRef({ dataType: null, maxYear: null });
  const [selectedResourceState, setSelectedResourceState] = useState("");
  const outcomeYearsFromRows = useMemo(() => yearsFromOutcomeRows(outcomeRows), [outcomeRows]);
  const outcomeYearsByApiLayer = useMemo(() => {
    const yearsByLayer = {};
    ["count", "adjusted"].forEach((apiLayer) => {
      const optionYears = optionValues(outcomeOptions[apiLayer] || {}, "years").map(Number).filter(isFiniteNumber);
      yearsByLayer[apiLayer] = optionYears.length ? optionYears.sort((a, b) => a - b) : outcomeYearsFromRows;
    });
    return yearsByLayer;
  }, [outcomeOptions, outcomeYearsFromRows]);
  const outcomeYears = useMemo(() => {
    const availableYears = outcomeYearsFromRows.length
      ? outcomeYearsFromRows
      : Array.from(new Set(Object.values(outcomeYearsByApiLayer).flat())).sort((a, b) => a - b);
    return availableYears.length ? availableYears : DEFAULT_YEARS;
  }, [outcomeYearsFromRows, outcomeYearsByApiLayer]);
  const crisis988Years = useMemo(() => yearsFrom988Rows(crisis988Rows), [crisis988Rows]);
  const years = useMemo(() => {
    const yearSet = new Set([...outcomeYears, ...crisis988Years]);
    const availableYears = Array.from(yearSet).sort((a, b) => a - b);
    return availableYears.length ? availableYears : DEFAULT_YEARS;
  }, [outcomeYears, crisis988Years]);
  const data = useMemo(() => buildData(outcomeRows, years, crisis988Rows), [outcomeRows, years, crisis988Rows]);
  const activeCopy = NAV_COPY[activeTab];
  const [tourStepIndex, setTourStepIndex] = useState(() => getPersistentFlag(DASHBOARD_TOUR_STORAGE_KEY) ? -1 : 0);
  const [tourSpotlight, setTourSpotlight] = useState(null);
  const activeTourStep = tourStepIndex >= 0 ? DASHBOARD_TOUR_STEPS[tourStepIndex] : null;
  const dashboardTourActive = Boolean(activeTourStep);

  const clearOverviewMetricMessageTimer = () => {
    if (overviewMetricMessageTimerRef.current) {
      window.clearTimeout(overviewMetricMessageTimerRef.current);
      overviewMetricMessageTimerRef.current = null;
    }
  };

  const clearDashboardInfoMessageTimer = () => {
    if (dashboardInfoMessageTimerRef.current) {
      window.clearTimeout(dashboardInfoMessageTimerRef.current);
      dashboardInfoMessageTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!overviewRevealRef.current) return undefined;

    const revealPanels = Array.from(overviewRevealRef.current.querySelectorAll(".overview-reveal-panel"));
    if (!revealPanels.length) return undefined;

    const setFocusState = (panel, ratio) => {
      panel.classList.toggle("is-visible", ratio >= 0.28);
      panel.classList.toggle("is-near", ratio > 0 && ratio < 0.28);
    };

    revealPanels.forEach((panel) => setFocusState(panel, 0));

    if (typeof IntersectionObserver === "undefined") {
      revealPanels.forEach((panel) => panel.classList.add("is-visible"));
      return undefined;
    }

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setFocusState(entry.target, entry.intersectionRatio);
        });
      },
      {
        root: null,
        rootMargin: "-12% 0px -18%",
        threshold: [0, 0.12, 0.28, 0.45, 0.68, 1],
      }
    );

    revealPanels.forEach((panel) => revealObserver.observe(panel));

    return () => revealObserver.disconnect();
  }, []);

  useEffect(() => () => {
    if (overviewMetricMessageTimerRef.current) {
      window.clearTimeout(overviewMetricMessageTimerRef.current);
      overviewMetricMessageTimerRef.current = null;
    }
    if (dashboardInfoMessageTimerRef.current) {
      window.clearTimeout(dashboardInfoMessageTimerRef.current);
      dashboardInfoMessageTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!categoryDropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (categoryDropdownRef.current?.contains(event.target)) return;
      setCategoryDropdownOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setCategoryDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [categoryDropdownOpen]);

  useEffect(() => {
    if (!activeTourStep) {
      setTourSpotlight(null);
      return undefined;
    }

    const scrollTarget = activeTourStep.scrollTarget ? document.querySelector(activeTourStep.scrollTarget) : null;
    if (scrollTarget) scrollTarget.scrollIntoView({ block: "start" });

    const updateSpotlight = () => {
      const target = document.querySelector(activeTourStep.target);
      if (!target) {
        setTourSpotlight(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      const padding = activeTourStep.padding || 8;
      const left = Math.max(12, rect.left - padding);
      const top = Math.max(12, rect.top - padding);
      const width = Math.min(window.innerWidth - left - 12, rect.width + padding * 2);
      const height = Math.min(window.innerHeight - top - 12, rect.height + padding * 2);
      setTourSpotlight({ top, left, width, height });
    };

    updateSpotlight();
    const refreshTimer = window.setTimeout(updateSpotlight, scrollTarget ? 120 : 80);
    window.addEventListener("resize", updateSpotlight);
    window.addEventListener("scroll", updateSpotlight, true);

    return () => {
      window.clearTimeout(refreshTimer);
      window.removeEventListener("resize", updateSpotlight);
      window.removeEventListener("scroll", updateSpotlight, true);
    };
  }, [activeTourStep]);

  useEffect(() => {
    if (!activeTourStep) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setPersistentFlag(DASHBOARD_TOUR_STORAGE_KEY);
        setTourStepIndex(-1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTourStep]);

  const exploreLayer = MAP_LAYER_BY_ID[exploreLayerId] || MAP_LAYER_BY_ID[DEFAULT_LAYER_ID];
  const nsduhTreatmentPoints = Array.isArray(nsduhTreatmentData?.points)
    ? nsduhTreatmentData.points
      .filter((point) => Number.isFinite(Number(point?.year)) && isFiniteNumber(point?.value))
      .map((point) => ({
        year: Number(point.year),
        value: Number(point.value),
        segment: typeof point.segment === "string" ? point.segment : "",
      }))
      .sort((left, right) => left.year - right.year)
    : [];
  const nsduhTreatmentSourceLinks = Array.isArray(nsduhTreatmentData?.sourceLinks)
    ? nsduhTreatmentData.sourceLinks.filter((link) => link?.label && link?.url)
    : [];
  const nsduhTreatmentWarnings = Array.isArray(nsduhTreatmentData?.warnings)
    ? nsduhTreatmentData.warnings.filter(Boolean)
    : [];
  const nationalMetric = nationalMetricId === NSDUH_TREATMENT_SERIES.id
    ? {
      ...NSDUH_TREATMENT_SERIES,
      points: nsduhTreatmentPoints,
      sourceLinks: nsduhTreatmentSourceLinks,
      warnings: nsduhTreatmentWarnings,
    }
    : null;
  const nationalTreatmentSourceMeta = nationalMetric
    ? {
      label: nsduhTreatmentLoading
        ? "Loading live SAMHSA NSDUH annual estimates"
        : nsduhTreatmentError
          ? "Live SAMHSA NSDUH data unavailable"
          : "SAMHSA NSDUH annual estimates · National only · Live HTML",
      links: nationalMetric.sourceLinks,
    }
    : null;

  useEffect(() => {
    let cancelled = false;

    if (nationalMetricId !== NSDUH_TREATMENT_SERIES.id) {
      setNsduhTreatmentLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setNsduhTreatmentData(null);
    setNsduhTreatmentLoading(true);
    setNsduhTreatmentError("");
    fetchNsduhTreatmentSeries()
      .then((series) => {
        if (cancelled) return;
        if (!Array.isArray(series?.points) || !series.points.length) {
          throw new Error("SAMHSA did not return any NSDUH treatment estimates.");
        }
        setNsduhTreatmentData(series);
      })
      .catch((error) => {
        if (cancelled) return;
        setNsduhTreatmentData(null);
        setNsduhTreatmentError(error?.message || "Live NSDUH treatment data are unavailable.");
      })
      .finally(() => {
        if (!cancelled) setNsduhTreatmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nationalMetricId]);

  const isNationalOnlyMetric = Boolean(nationalMetric);
  const selectedStateLimit = isNationalOnlyMetric ? 0 : dataDashboardView === "compare" ? MAX_SELECTED_STATES : 1;
  const selectedRows = useMemo(
    () => selectedStates.map((code) => data.find((row) => row.code === code)).filter(Boolean),
    [data, selectedStates]
  );
  const selectedTrendLayer = isNationalOnlyMetric ? null : exploreLayer;
  const selectedTrendType = isNationalOnlyMetric ? "national" : isOutcomeLayer(selectedTrendLayer) ? "outcome" : "indicator";
  const trendOutcomeLayer = !isNationalOnlyMetric && isOutcomeLayer(selectedTrendLayer) ? selectedTrendLayer : null;
  const showOutcomeNationalTrendControl = Boolean(trendOutcomeLayer);
  const effectiveShowNationalTrend = showOutcomeNationalTrendControl ? showNationalTrend : true;
  const selectedDataSelectionValue = isNationalOnlyMetric
    ? nationalMetric.selectionValue
    : is988Layer(exploreLayer) ? `kpi:${explore988Metric}` : `layer:${exploreLayer.id}`;
  const yearsForLayer = (layerOrId) => {
    const layerId = typeof layerOrId === "string" ? layerOrId : layerOrId?.id;
    if (is988Layer(layerId)) return crisis988Years.length ? crisis988Years : DEFAULT_YEARS;
    if (isOutcomeLayer(layerId)) {
      const layerYears = outcomeYearsByApiLayer[outcomeApiLayerFor(layerId)] || [];
      return layerYears.length ? layerYears : outcomeYears;
    }
    return years;
  };
  const exploreYearOptions = isNationalOnlyMetric ? nationalMetric.points.map((point) => point.year) : yearsForLayer(exploreLayer);
  const nationalTreatmentSourceLinksForSelectedYear = nationalMetric
    ? nationalMetric.sourceLinks.filter((link) => {
      if (!Array.isArray(link.years) || !link.years.length) return true;
      return link.years.map(Number).includes(Number(mapYear));
    })
    : [];
  const nationalTreatmentSourceMetaForSelectedYear = nationalMetric
    ? { ...nationalTreatmentSourceMeta, links: nationalTreatmentSourceLinksForSelectedYear }
    : null;
  const exploreDataType = isNationalOnlyMetric ? nationalMetric.id : is988Layer(exploreLayer) ? "crisis988" : "outcome";
  const activeOutcomeLayer = trendOutcomeLayer;
  const activeOutcomeApiLayer = outcomeApiLayerFor(activeOutcomeLayer);
  const activeOutcomeOptions = useMemo(
    () => outcomeOptions[activeOutcomeApiLayer] || {},
    [outcomeOptions, activeOutcomeApiLayer]
  );
  const outcomeFilters = useMemo(() => ({
    breakdownType,
    gender,
    race,
    cause,
    education,
    placeOfDeath,
  }), [breakdownType, gender, race, cause, education, placeOfDeath]);
  const exploreBreakdownOptions = useMemo(() => {
    if (isNationalOnlyMetric || !isOutcomeLayer(exploreLayer)) return [];
    const layerOptions = outcomeOptions[outcomeApiLayerFor(exploreLayer)] || {};
    const available = optionValues(layerOptions, "breakdownTypes");
    return EXPLORE_BREAKDOWN_TYPES.filter((type) => {
      if (type === "overall") return true;
      if (!available.includes(type)) return false;
      if (outcomeApiLayerFor(exploreLayer) === "adjusted" && ["education", "place_of_death"].includes(type)) return false;
      return true;
    });
  }, [isNationalOnlyMetric, exploreLayer, outcomeOptions]);
  const dashboardBreakdownOptions = useMemo(
    () => !isNationalOnlyMetric && isOutcomeLayer(exploreLayer)
      ? (exploreBreakdownOptions.length ? exploreBreakdownOptions : ["overall"])
      : ["overall"],
    [isNationalOnlyMetric, exploreLayer, exploreBreakdownOptions]
  );
  const dashboardBreakdownType = !isNationalOnlyMetric && isOutcomeLayer(exploreLayer) && dashboardBreakdownOptions.includes(breakdownType)
    ? breakdownType
    : "overall";
  const insightCategoryField = categoryFieldForBreakdown(dashboardBreakdownType);
  const insightCategoryOptions = useMemo(
    () => !isNationalOnlyMetric && isOutcomeLayer(exploreLayer) ? selectableCategoryValues(activeOutcomeOptions, dashboardBreakdownType) : [],
    [isNationalOnlyMetric, exploreLayer, activeOutcomeOptions, dashboardBreakdownType]
  );
  const outcomeFilterValues = { gender, race, cause, education, placeOfDeath };
  const selectedOutcomeCategoryValue = insightCategoryField
    ? outcomeFilterValues[insightCategoryField.key] || "All"
    : "All";
  const effectiveOutcomeCategoryValue = insightCategoryOptions.includes(selectedOutcomeCategoryValue)
    ? selectedOutcomeCategoryValue
    : insightCategoryOptions[0] || "All";
  const selectedDashboardCategoryValues = useMemo(() => {
    if (!insightCategoryField) return [];
    return normalizeDashboardCategorySelections(
      dashboardCategorySelections[insightCategoryField.key],
      insightCategoryOptions
    );
  }, [dashboardCategorySelections, insightCategoryField, insightCategoryOptions]);
  const overviewOutcomeRequestFilters = useMemo(() => {
    if (isNationalOnlyMetric || dataDashboardView !== "overview" || !isOutcomeLayer(exploreLayer)) return { breakdownType: "overall" };
    if (dashboardBreakdownType === "overall" || !insightCategoryField) return { breakdownType: "overall" };
    if (!effectiveOutcomeCategoryValue || effectiveOutcomeCategoryValue === "All") return { breakdownType: "overall" };
    return { breakdownType: dashboardBreakdownType, [insightCategoryField.key]: effectiveOutcomeCategoryValue };
  }, [isNationalOnlyMetric, dataDashboardView, exploreLayer, dashboardBreakdownType, insightCategoryField, effectiveOutcomeCategoryValue]);
  const exploreYearValues = data.map((row) => layerValueForYear(row, exploreLayer.id, mapYear)).filter(isFiniteNumber);
  const nationalExploreYearValue = exploreYearValues.length
    ? exploreLayer.id === "suicideCount"
      ? exploreYearValues.reduce((sum, value) => sum + Number(value), 0)
      : Number((exploreYearValues.reduce((sum, value) => sum + Number(value), 0) / exploreYearValues.length).toFixed(1))
    : null;
  const rateForSelectedYear = (row, layerId = exploreLayer.id) => layerValueForYear(row, layerId, mapYear);
  const rowsWithExploreValues = data.filter((row) => isFiniteNumber(rateForSelectedYear(row, exploreLayer.id)));
  const highestExploreYearRow = rowsWithExploreValues.reduce(
    (highest, row) => (rateForSelectedYear(row, exploreLayer.id) > rateForSelectedYear(highest, exploreLayer.id) ? row : highest),
    rowsWithExploreValues[0]
  );
  const lowestExploreYearRow = rowsWithExploreValues.reduce(
    (lowest, row) => (rateForSelectedYear(row, exploreLayer.id) < rateForSelectedYear(lowest, exploreLayer.id) ? row : lowest),
    rowsWithExploreValues[0]
  );
  const nationalSeriesByLayer = Object.fromEntries(MAP_LAYERS.map((layer) => [
    layer.id,
    yearsForLayer(layer).map((year) => {
      const values = data.map((row) => layerValueForYear(row, layer.id, year)).filter(isFiniteNumber);
      const total = values.reduce((sum, value) => sum + Number(value), 0);
      return {
        year,
        value: values.length
          ? layer.id === "suicideCount" ? total : Number((total / values.length).toFixed(1))
          : null,
      };
    }),
  ]));

  useEffect(() => {
    let cancelled = false;

    if (!is988Layer(exploreLayer)) {
      setCrisis988Loading(false);
      return () => {
        cancelled = true;
      };
    }

    setCrisis988Loading(true);
    fetch988Indicator({ metric: explore988Metric })
      .then((rows) => {
        if (cancelled) return;
        setCrisis988Rows(Array.isArray(rows) ? rows : []);
        setCrisis988Error("");
      })
      .catch((error) => {
        if (cancelled) return;
        setCrisis988Rows([]);
        setCrisis988Error(error?.message || "988 KPI data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setCrisis988Loading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [exploreLayer, explore988Metric]);

  useEffect(() => {
    let cancelled = false;
    setOutcomeOptionsLoading(true);

    fetchOutcomeOptions()
      .then((options) => {
        if (cancelled) return;
        setOutcomeOptions(options && typeof options === "object" ? options : {});
        setOutcomeError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setOutcomeOptions({});
        setOutcomeError(error?.message || "CDC outcome filter options are unavailable.");
      })
      .finally(() => {
        if (!cancelled) setOutcomeOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!activeOutcomeLayer) {
      setOutcomeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setOutcomeLoading(true);
    fetchSuicideOutcomes(overviewOutcomeRequestFilters)
      .then((rows) => {
        if (cancelled) return;
        setOutcomeRows(Array.isArray(rows) ? rows : []);
        setOutcomeError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setOutcomeRows([]);
        setOutcomeError(error?.message || "CDC outcome data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setOutcomeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeOutcomeLayer, overviewOutcomeRequestFilters]);

  useEffect(() => {
    if (selectedStates.length > selectedStateLimit) {
      setSelectedStates((current) => current.slice(0, selectedStateLimit));
      setStateSelectionMessage("");
    }
  }, [selectedStateLimit, selectedStates.length]);

  useEffect(() => {
    if (!isOutcomeLayer(exploreLayer)) {
      setCategoryDropdownOpen(false);
      return;
    }
    if (dashboardBreakdownOptions.length && !dashboardBreakdownOptions.includes(breakdownType)) {
      setBreakdownType("overall");
      setGender("All");
      setRace("All");
      setCause("All");
      setEducation("All");
      setPlaceOfDeath("All");
      setDashboardCategorySelections(emptyDashboardCategorySelections());
      setCategoryDropdownOpen(false);
      setBreakdownSelectionExpanded(false);
      return;
    }

    if (dashboardBreakdownType === "overall" || !insightCategoryField) {
      setCategoryDropdownOpen(false);
      setBreakdownSelectionExpanded(false);
      return;
    }

    if (insightCategoryOptions.length && !insightCategoryOptions.includes(selectedOutcomeCategoryValue)) {
      const nextCategory = defaultCategoryForBreakdown(activeOutcomeOptions, dashboardBreakdownType);
      const setters = {
        gender: setGender,
        race: setRace,
        cause: setCause,
        education: setEducation,
        placeOfDeath: setPlaceOfDeath,
      };
      setters[insightCategoryField.key]?.(nextCategory);
    }

    if (dataDashboardView !== "overview") {
      setDashboardCategorySelections((current) => {
        const currentValues = current[insightCategoryField.key] || [];
        const validValues = normalizeDashboardCategorySelections(currentValues, insightCategoryOptions);
        const defaultValue = defaultCategoryForBreakdown(activeOutcomeOptions, dashboardBreakdownType);
        const fallbackValues = defaultValue && defaultValue !== "All" && insightCategoryOptions.includes(defaultValue)
          ? [defaultValue]
          : insightCategoryOptions.slice(0, 1);
        const nextValues = validValues.length || dashboardCategorySelectionTouchedRef.current ? validValues : fallbackValues;
        if (sameStringArray(currentValues, nextValues)) return current;
        return { ...current, [insightCategoryField.key]: nextValues };
      });
    }
  }, [
    exploreLayer,
    dashboardBreakdownOptions,
    dashboardBreakdownType,
    breakdownType,
    insightCategoryField,
    insightCategoryOptions,
    selectedOutcomeCategoryValue,
    activeOutcomeOptions,
    dataDashboardView,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!is988Layer(exploreLayer)) {
      setCrisis988ComparisonData(null);
      setCrisis988ComparisonError("");
      setCrisis988ComparisonLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (!showExploreNationalComparison && !selectedStates.length) {
      setCrisis988ComparisonData({ groups: [], message: "Use the Location dropdown to select up to 2 states, or turn on national comparison to view 988 KPI data." });
      setCrisis988ComparisonError("");
      setCrisis988ComparisonLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setCrisis988ComparisonLoading(true);
    fetch988Comparison({
      year: Number(mapYear),
      metric: explore988Metric,
      states: selectedStates.slice(0, MAX_SELECTED_STATES),
      includeNational: showExploreNationalComparison,
    })
      .then((comparison) => {
        if (cancelled) return;
        setCrisis988ComparisonData(comparison && typeof comparison === "object" ? comparison : null);
        setCrisis988ComparisonError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setCrisis988ComparisonData(null);
        setCrisis988ComparisonError(error?.message || "988 KPI comparison data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setCrisis988ComparisonLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [exploreLayer, mapYear, explore988Metric, selectedStates, showExploreNationalComparison]);

  useEffect(() => {
    let cancelled = false;

    if (dataDashboardView !== "overview" || !isOutcomeLayer(exploreLayer)) {
      setOverviewNationalOutcomeData(null);
      setOverviewNationalOutcomeError("");
      setOverviewNationalOutcomeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (dashboardBreakdownType !== "overall" && insightCategoryField && (!effectiveOutcomeCategoryValue || effectiveOutcomeCategoryValue === "All")) {
      setOverviewNationalOutcomeData({ series: [], message: "Select a subgroup category to view the national value." });
      setOverviewNationalOutcomeError("");
      setOverviewNationalOutcomeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const nationalFilters = {
      layer: outcomeApiLayerFor(exploreLayer),
      breakdownType: dashboardBreakdownType,
      states: [],
      includeNational: true,
    };
    if (dashboardBreakdownType !== "overall" && insightCategoryField) {
      nationalFilters[insightCategoryField.optionKey] = [effectiveOutcomeCategoryValue];
    }

    setOverviewNationalOutcomeLoading(true);
    fetchOutcomeTrend(nationalFilters)
      .then((trend) => {
        if (cancelled) return;
        setOverviewNationalOutcomeData(trend && typeof trend === "object" ? trend : null);
        setOverviewNationalOutcomeError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setOverviewNationalOutcomeData(null);
        setOverviewNationalOutcomeError(error?.message || "National outcome value is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setOverviewNationalOutcomeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dataDashboardView, exploreLayer, dashboardBreakdownType, insightCategoryField, effectiveOutcomeCategoryValue]);

  useEffect(() => {
    let cancelled = false;
    if (!trendOutcomeLayer) {
      setOutcomeTrendData(null);
      setOutcomeTrendError("");
      setOutcomeTrendLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (dashboardBreakdownType !== "overall" && insightCategoryField && !selectedDashboardCategoryValues.length) {
      setOutcomeTrendData({ series: [], message: "Select one or more subgroup categories to view outcome trends." });
      setOutcomeTrendError("");
      setOutcomeTrendLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const trendFilters = {
      layer: outcomeApiLayerFor(trendOutcomeLayer),
      breakdownType: dashboardBreakdownType,
      states: selectedStates.slice(0, MAX_SELECTED_STATES),
      includeNational: effectiveShowNationalTrend,
    };
    if (dashboardBreakdownType !== "overall" && insightCategoryField) {
      trendFilters[insightCategoryField.optionKey] = selectedDashboardCategoryValues;
    }

    setOutcomeTrendLoading(true);
    fetchOutcomeTrend(trendFilters)
      .then((trend) => {
        if (cancelled) return;
        setOutcomeTrendData(trend && typeof trend === "object" ? trend : null);
        setOutcomeTrendError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setOutcomeTrendData(null);
        setOutcomeTrendError(error?.message || "Outcome trend data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setOutcomeTrendLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [trendOutcomeLayer, selectedStates, dashboardBreakdownType, selectedDashboardCategoryValues, effectiveShowNationalTrend, insightCategoryField]);

  useEffect(() => {
    let cancelled = false;

    if (!is988Layer(selectedTrendLayer)) {
      setCrisis988TrendData(null);
      setCrisis988TrendError("");
      setCrisis988TrendLoading(false);
      return () => {
        cancelled = true;
      };
    }

    if (!showNational988Trend && !selectedStates.length) {
      setCrisis988TrendData({ series: [], message: "Use the Location dropdown to select one state, or turn on national trend to view 988 KPI trends." });
      setCrisis988TrendError("");
      setCrisis988TrendLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setCrisis988TrendLoading(true);
    fetch988Trend({
      metric: explore988Metric,
      states: selectedStates.slice(0, MAX_SELECTED_STATES),
      includeNational: showNational988Trend,
      granularity: align988TrendYearly ? "yearly" : "monthly",
    })
      .then((trend) => {
        if (cancelled) return;
        setCrisis988TrendData(trend && typeof trend === "object" ? trend : null);
        setCrisis988TrendError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setCrisis988TrendData(null);
        setCrisis988TrendError(error?.message || "988 KPI trend data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setCrisis988TrendLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTrendLayer, explore988Metric, selectedStates, showNational988Trend, align988TrendYearly]);

  useEffect(() => {
    if (!exploreYearOptions.length) return;

    const latestYear = exploreYearOptions[exploreYearOptions.length - 1];
    const currentYear = Number(mapYear);
    const previousDomain = previousExploreYearDomainRef.current;
    const dataTypeChanged = previousDomain.dataType !== exploreDataType;
    const wasAtPreviousMax = previousDomain.maxYear !== null && currentYear === previousDomain.maxYear;
    const latestYearAdvanced = previousDomain.maxYear !== null && latestYear > previousDomain.maxYear;

    previousExploreYearDomainRef.current = { dataType: exploreDataType, maxYear: latestYear };

    if (dataTypeChanged || !exploreYearOptions.includes(currentYear) || (wasAtPreviousMax && latestYearAdvanced)) {
      setMapYear(latestYear);
    }
  }, [exploreDataType, exploreYearOptions, mapYear]);

  useEffect(() => {
    if (!isOutcomeLayer(exploreLayer)) {
      setOutcomeError("");
      setOutcomeLoading(false);
    }
  }, [exploreLayer]);



  useEffect(() => {
    let cancelled = false;

    if (!isOutcomeLayer(exploreLayer) || dashboardBreakdownType === "overall") {
      setOutcomeBreakdownData(null);
      setOutcomeBreakdownError("");
      setOutcomeBreakdownLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setOutcomeBreakdownLoading(true);
    fetchOutcomeBreakdown({
      year: Number(mapYear),
      layer: outcomeApiLayerFor(exploreLayer),
      breakdownType: dashboardBreakdownType,
      states: selectedStates.slice(0, MAX_SELECTED_STATES),
    })
      .then((breakdown) => {
        if (cancelled) return;
        setOutcomeBreakdownData(breakdown && typeof breakdown === "object" ? breakdown : null);
        setOutcomeBreakdownError("");
      })
      .catch((error) => {
        if (cancelled) return;
        setOutcomeBreakdownData(null);
        setOutcomeBreakdownError(error?.message || "Breakdown data is unavailable.");
      })
      .finally(() => {
        if (!cancelled) setOutcomeBreakdownLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [exploreLayer, dashboardBreakdownType, mapYear, selectedStates]);



  useEffect(() => {
    const hasVisibleBreakdown = isOutcomeLayer(exploreLayer) && dashboardBreakdownType !== "overall";
    if (!hasVisibleBreakdown || hasScrolledToBreakdownRef.current) return;

    hasScrolledToBreakdownRef.current = true;
    setSessionFlag("explore_breakdown_panel_seen");
    window.setTimeout(() => {
      breakdownPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, [exploreLayer, dashboardBreakdownType]);

  const updateOutcomeFilter = (key, value) => {
    const setters = {
      gender: setGender,
      race: setRace,
      cause: setCause,
      education: setEducation,
      placeOfDeath: setPlaceOfDeath,
    };

    if (key === "breakdownType") {
      setBreakdownType(value);
      setGender("All");
      setRace("All");
      setCause("All");
      setEducation("All");
      setPlaceOfDeath("All");
      const field = categoryFieldForBreakdown(value);
      if (field) setters[field.key]?.(defaultCategoryForBreakdown(activeOutcomeOptions, value));
      return;
    }

    setters[key]?.(value);
  };

  const outcomeFiltersForLayer = (layer) => {
    const layerOptions = outcomeOptions[outcomeApiLayerFor(layer)] || {};
    return sanitizeOutcomeFilters(outcomeFilters, layerOptions);
  };

  const exploreYearControl = (className = "toolbar-field year-field") => {
    const selectableYears = exploreYearOptions.length ? exploreYearOptions : years;
    const selectedYear = selectableYears.includes(Number(mapYear)) ? Number(mapYear) : selectableYears[selectableYears.length - 1];
    return (
      <label className={className}>
        Year
        <select
          className="toolbar-select"
          value={selectedYear}
          onChange={(event) => setMapYear(Number(event.target.value))}
        >
          {selectableYears.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </label>
    );
  };


  const toggleState = (code) => {
    if (isNationalOnlyMetric || !code || !STATE_BY_CODE[code]) return;
    setSelectedStates((current) => {
      if (current.includes(code)) {
        setStateSelectionMessage("");
        return current.filter((item) => item !== code);
      }
      if (selectedStateLimit === 1) {
        setStateSelectionMessage("");
        return [code];
      }
      if (current.length >= selectedStateLimit) {
        setStateSelectionMessage(`You can compare up to ${selectedStateLimit} states at a time. Remove a selected state to add another.`);
        return current;
      }
      setStateSelectionMessage("");
      return [...current, code];
    });
  };

  const removeState = (code) => {
    setSelectedStates((current) => current.filter((item) => item !== code));
    setStateSelectionMessage("");
  };

  const mapDataForLayer = (layerId, neutralSelectionOnly = false) => {
    const layer = MAP_LAYER_BY_ID[layerId] || MAP_LAYER_BY_ID[DEFAULT_LAYER_ID];
    const rawValues = neutralSelectionOnly
      ? data.map(() => 1)
      : data.map((row) => layerValueForYear(row, layer.id, mapYear));
    const numericValues = rawValues.filter(isFiniteNumber).map(Number);
    const is988 = is988Layer(layer);
    const valueFormatter = (value) => formatLayerValue(value, layer, explore988Metric);
    const dynamicZMax = is988 && numericValues.length
      ? Math.max(KPI_988_BY_METRIC[explore988Metric]?.valueType === "percent" ? 100 : 1, Math.ceil(Math.max(...numericValues) * 1.08))
      : layer.zmax;
    const discreteScale = neutralSelectionOnly ? null : discreteMapScaleForLayer(layer, rawValues, explore988Metric, dynamicZMax);
    const values = neutralSelectionOnly ? rawValues : rawValues.map((value) => discreteScale.binValue(value));
    const traces = [
      {
        type: "choropleth",
        locationmode: "USA-states",
        locations: data.map((row) => row.code),
        z: values,
        text: data.map((row) => row.name),
        customdata: data.map((row) => {
          const yearValue = layerValueForYear(row, layer.id, mapYear);
          return [
            row.code,
            valueFormatter(latestLayerValue(row, layer.id)),
            layer.label,
            valueFormatter(yearValue),
            discreteScale ? discreteScale.binLabel(yearValue) : "",
          ];
        }),
        colorscale: neutralSelectionOnly
          ? [[0, "#eef3f5"], [1, "#eef3f5"]]
          : discreteScale.colorscale,
        zmin: neutralSelectionOnly ? 0 : discreteScale.zmin,
        zmax: neutralSelectionOnly ? 1 : discreteScale.zmax,
        showscale: !neutralSelectionOnly,
        marker: { line: { color: neutralSelectionOnly ? "#cbd7dd" : "#ffffff", width: neutralSelectionOnly ? 0.9 : 0.8 } },
        colorbar: neutralSelectionOnly
          ? undefined
          : {
            thickness: 24,
            len: 0.72,
            y: 0.43,
            yanchor: "middle",
            tickmode: "array",
            tickvals: discreteScale.tickvals,
            ticktext: discreteScale.ticktext,
            tickfont: { size: 11, color: "#24313a" },
            outlinewidth: 1,
            outlinecolor: "rgba(93, 139, 160, 0.32)",
            xpad: 12,
          },
        hovertemplate: neutralSelectionOnly
          ? "<b>%{text}</b><br>Click to select<extra></extra>"
          : "<b>%{text}</b><br>%{customdata[3]}<extra></extra>",
      },
    ];
    if (selectedStates.length) {
      traces.push({
        type: "choropleth",
        locationmode: "USA-states",
        locations: selectedStates,
        z: selectedStates.map(() => 1),
        text: selectedStates.map((code) => STATE_BY_CODE[code]?.name || code),
        colorscale: [[0, "rgba(255,255,255,0)"], [1, "rgba(255,255,255,0)"]],
        showscale: false,
        hoverinfo: "skip",
        marker: { line: { color: "#111827", width: 2.4 } },
      });
    }
    return traces;
  };

  const mapLayout = (compact = false) => ({
    margin: compact ? { l: 0, r: 0, t: 0, b: 0 } : { l: 0, r: 112, t: 8, b: 0 },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    geo: {
      scope: "usa",
      projection: { type: "albers usa" },
      bgcolor: "rgba(0,0,0,0)",
      lakecolor: "#eaf6fb",
      landcolor: "#f8fcfd",
      showlakes: true,
      showland: true,
    },
    dragmode: false,
    autosize: true,
    showlegend: false,
  });

  const hoverYearShape = (year) => isFiniteNumber(year)
    ? [{
      type: "line",
      xref: "x",
      yref: "paper",
      x0: Number(year),
      x1: Number(year),
      y0: 0,
      y1: 1,
      line: { color: "#111827", width: 1.5, dash: "dash" },
    }]
    : [];

  const chartLayout = (title, layer, xYears = yearsForLayer(layer), hoverYear = null) => {
    const axisYears = xYears.length ? xYears : yearsForLayer(layer);
    const firstYear = axisYears[0];
    const lastYear = axisYears[axisYears.length - 1];
    return {
      title: title ? { text: title, x: 0, xanchor: "left", font: { size: 15, color: "#24313a" } } : undefined,
      margin: { l: 58, r: 18, t: title ? 44 : 18, b: 46 },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(255,255,255,0.58)",
      hovermode: "x unified",
      shapes: hoverYearShape(hoverYear),
      xaxis: {
        title: "Year",
        range: [firstYear - 0.2, lastYear + 0.2],
        tickmode: "linear",
        dtick: 1,
        gridcolor: "rgba(93,139,160,0.16)",
      },
      yaxis: {
        title: layer.valueLabel,
        ticksuffix: layer.valueFormat === "percent" ? "%" : undefined,
        rangemode: "tozero",
        gridcolor: "rgba(93,139,160,0.16)",
      },
      legend: { orientation: "h", y: -0.24, x: 0 },
    };
  };

  const kpi988TrendLayout = (metric, yearlyAligned = true, xYears = crisis988Years, hoverYear = null) => {
    const option = KPI_988_BY_METRIC[metric] || KPI_988_BY_METRIC[DEFAULT_988_METRIC];
    const axisYears = xYears.length ? xYears : yearsForLayer("crisis988");
    const firstYear = axisYears[0];
    const lastYear = axisYears[axisYears.length - 1];
    return {
      margin: { l: 58, r: 18, t: 18, b: 54 },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(255,255,255,0.58)",
      hovermode: "x unified",
      shapes: yearlyAligned ? hoverYearShape(hoverYear) : [],
      xaxis: yearlyAligned
        ? {
          title: "Year",
          range: [firstYear - 0.2, lastYear + 0.2],
          tickmode: "linear",
          dtick: 1,
          gridcolor: "rgba(93,139,160,0.16)",
          automargin: true,
        }
        : {
          title: "Month",
          type: "category",
          gridcolor: "rgba(93,139,160,0.16)",
          automargin: true,
        },
      yaxis: {
        title: option.yAxisLabel,
        rangemode: "tozero",
        gridcolor: "rgba(93,139,160,0.16)",
      },
      legend: { orientation: "h", y: -0.24, x: 0 },
    };
  };

  const historicalTracesForLayer = (layer) => {
    const layerYears = yearsForLayer(layer);
    const selectedTraces = selectedRows.map((row, index) => ({
      type: "scatter",
      mode: "lines+markers",
      name: row.code,
      x: layerYears,
      y: layerYears.map((year) => layerValueForYear(row, layer.id, year)),
      line: { width: 3, color: STATE_COLORS[index % STATE_COLORS.length] },
      marker: { size: 7 },
      hovertemplate: row.name + "<br>%{x}: %{y:.1f} " + layer.shortLabel + "<extra></extra>",
    }));
    if (selectedTraces.length) return selectedTraces;
    return [
      {
        type: "scatter",
        mode: "lines",
        name: "United States average",
        x: nationalSeriesByLayer[layer.id].map((point) => point.year),
        y: nationalSeriesByLayer[layer.id].map((point) => point.value),
        line: { width: 3, color: "#5d8ba0" },
      },
    ];
  };

  const insightHistoryForLayer = (layer) => {
    if (is988Layer(layer)) {
      const trendSeries = Array.isArray(crisis988TrendData?.series) ? crisis988TrendData.series : [];
      return {
        layer: layer.label,
        metric: KPI_988_BY_METRIC[explore988Metric]?.label || layer.metric,
        filters: { metric: explore988Metric, includeNational: showNational988Trend, granularity: align988TrendYearly ? "yearly" : "monthly" },
        selectedStates: trendSeries
          .filter((series) => series.type === "state")
          .map((series) => ({
            state: series.label,
            stateFips: series.state_fips,
            series: series.values,
          })),
        nationalAverageSeries: trendSeries.find((series) => series.type === "national")?.values || [],
      };
    }

    const layerYears = yearsForLayer(layer);
    return {
      layer: layer.label,
      metric: layer.metric,
      filters: isOutcomeLayer(layer) ? outcomeFiltersForLayer(layer) : null,
      selectedStates: selectedRows.map((row) => ({
        state: row.name,
        code: row.code,
        latestYear: layerYears[layerYears.length - 1],
        latestValue: latestLayerValue(row, layer.id),
        tenYearChange: tenYearLayerChange(row, layer.id),
        series: layerYears.map((year) => ({
          year,
          value: layerValueForYear(row, layer.id, year),
        })),
      })),
      nationalAverageSeries: nationalSeriesByLayer[layer.id],
    };
  };

  const analysisRowClass = (baseClass) => baseClass;

  const outcomeTrendSeries = Array.isArray(outcomeTrendData?.series) ? outcomeTrendData.series : [];
  const outcomeTrendYears = yearsFromTrendSeries(outcomeTrendSeries);
  const outcomeTrendMessage = outcomeTrendData?.message || "";
  const crisis988TrendSeries = Array.isArray(crisis988TrendData?.series) ? crisis988TrendData.series : [];
  const crisis988TrendYears = yearsFromTrendSeries(crisis988TrendSeries);
  const crisis988TrendMessage = crisis988TrendData?.message || "";
  const outcomeTrendAxisYears = outcomeTrendYears;
  const crisis988TrendAxisYears = crisis988TrendYears;
  const sharedInsightHoverYear = hoveredInsightYear;

  const handleInsightTrendHover = (event) => {
    if (is988Layer(selectedTrendLayer) && !align988TrendYearly) return;
    const year = Number(event?.points?.[0]?.x);
    if (isFiniteNumber(year)) setHoveredInsightYear(year);
  };

  const clearInsightTrendHover = () => {
    if (hoveredInsightYear !== null) setHoveredInsightYear(null);
  };

  const dashboardCategoryDropdownDisabled = !isOutcomeLayer(exploreLayer) || outcomeOptionsLoading || !insightCategoryOptions.length;
  const dashboardCategoryDropdownSummary = selectedDashboardCategoryValues.length === 1
    ? selectedDashboardCategoryValues[0]
    : selectedDashboardCategoryValues.length
      ? `${selectedDashboardCategoryValues.length} selected`
      : "Select categories";

  const removeDashboardCategorySelection = (value) => {
    if (!insightCategoryField) return;
    dashboardCategorySelectionTouchedRef.current = true;
    setDashboardCategorySelections((current) => {
      const currentValues = normalizeDashboardCategorySelections(current[insightCategoryField.key], insightCategoryOptions);
      const nextValues = currentValues.filter((item) => item !== value);
      if (sameStringArray(currentValues, nextValues)) return current;
      return { ...current, [insightCategoryField.key]: nextValues };
    });
  };

  const toggleDashboardCategorySelection = (value) => {
    if (!insightCategoryField || !insightCategoryOptions.includes(value)) return;
    dashboardCategorySelectionTouchedRef.current = true;
    setDashboardCategorySelections((current) => {
      const currentValues = normalizeDashboardCategorySelections(current[insightCategoryField.key], insightCategoryOptions);
      const alreadySelected = currentValues.includes(value);
      const nextValues = alreadySelected
        ? currentValues.filter((item) => item !== value)
        : currentValues.length >= MAX_DASHBOARD_CATEGORY_SELECTIONS
          ? currentValues
          : [...currentValues, value];
      if (sameStringArray(currentValues, nextValues)) return current;
      return { ...current, [insightCategoryField.key]: nextValues };
    });
  };

  const renderSelectedBreakdownCategoryChips = () => {
    if (!isOutcomeLayer(exploreLayer) || dashboardBreakdownType === "overall" || !insightCategoryField || !selectedDashboardCategoryValues.length) return null;
    const expandable = selectedDashboardCategoryValues.length > 1 || selectedDashboardCategoryValues.some((value) => value.length > 28);

    return (
      <div className="breakdown-selection-tray" aria-label="Selected breakdown categories">
        <div className={`breakdown-selection-list ${breakdownSelectionExpanded ? "expanded" : ""}`}>
          {selectedDashboardCategoryValues.map((value) => (
            <button
              type="button"
              key={value}
              className="breakdown-selection-chip"
              onClick={() => removeDashboardCategorySelection(value)}
              title={`Remove ${value}`}
            >
              <span className="breakdown-selection-chip-label">{value}</span>
              <span className="breakdown-selection-chip-remove" aria-hidden="true">x</span>
            </button>
          ))}
        </div>
        {expandable ? (
          <button
            type="button"
            className="breakdown-selection-expand"
            aria-expanded={breakdownSelectionExpanded}
            onClick={() => setBreakdownSelectionExpanded((expanded) => !expanded)}
          >
            {breakdownSelectionExpanded ? "Less" : "More"}
          </button>
        ) : null}
      </div>
    );
  };

  const outcomeTrendTraces = (xYears = outcomeTrendAxisYears) => outcomeTrendSeries.map((series, index) => {
    const values = valueByYear(series.values || []);
    const y = xYears.map((year) => values.get(Number(year)) ?? null);
    return {
      type: "scatter",
      mode: "lines+markers",
      name: series.label,
      x: xYears,
      y,
      customdata: y.map((value) => formatTrendHoverValue(value)),
      connectgaps: false,
      line: {
        width: series.type === "national" ? 3.4 : 2.7,
        color: series.type === "national" ? "#111827" : TREND_LINE_COLORS[index % TREND_LINE_COLORS.length],
        dash: series.type === "national" ? "dash" : "solid",
      },
      marker: { size: series.type === "national" ? 7 : 6 },
      hovertemplate: series.label + "<br>%{x}: %{customdata}<extra></extra>",
    };
  });

  const crisis988TrendTraces = (xYears = crisis988TrendAxisYears) => crisis988TrendSeries.map((series, index) => {
    const values = valueByYear(series.values || []);
    const x = align988TrendYearly ? xYears : (series.values || []).map((point) => point.date);
    const y = align988TrendYearly ? xYears.map((year) => values.get(Number(year)) ?? null) : (series.values || []).map((point) => point.value);
    const customdata = align988TrendYearly
      ? y.map((value) => formatTrendHoverValue(value, (item) => format988Value(item, explore988Metric)))
      : (series.values || []).map((point) => format988Value(point.value, explore988Metric));
    return {
      type: "scatter",
      mode: "lines+markers",
      name: series.label,
      x,
      y,
      customdata,
      connectgaps: false,
      line: {
        width: series.type === "national" ? 3.4 : 2.7,
        color: series.type === "national" ? "#111827" : TREND_LINE_COLORS[index % TREND_LINE_COLORS.length],
        dash: series.type === "national" ? "dash" : "solid",
      },
      marker: { size: series.type === "national" ? 7 : 6 },
      hovertemplate: series.label + "<br>%{x}: %{customdata}<extra></extra>",
    };
  });

  const insightOutcomeControls = () => {
    if (!trendOutcomeLayer) return null;
    const selectedCategoryChips = renderSelectedBreakdownCategoryChips();
    if (!showOutcomeNationalTrendControl && !selectedCategoryChips) return null;

    return (
      <div className="insight-panel-controls" aria-label="Outcome trend filters">
        {selectedCategoryChips}
        {showOutcomeNationalTrendControl ? (
          <label className="insight-national-toggle">
            <input
              type="checkbox"
              checked={showNationalTrend}
              onChange={(event) => setShowNationalTrend(event.target.checked)}
            />
            <span>Show national trend</span>
          </label>
        ) : null}
      </div>
    );
  };

  const insight988Controls = () => (
    <div className="insight-panel-controls" aria-label="988 KPI trend filters">
      <label className="insight-national-toggle">
        <input
          type="checkbox"
          checked={showNational988Trend}
          onChange={(event) => setShowNational988Trend(event.target.checked)}
        />
        <span>Show national trend</span>
      </label>
      <div className="insight-granularity-toggle" role="group" aria-label="Monthly or yearly 988 data">
        <span>Monthly/Yearly data</span>
        <div className="insight-granularity-options">
          <button
            type="button"
            className={!align988TrendYearly ? "active" : ""}
            onClick={() => setAlign988TrendYearly(false)}
          >
            Monthly
          </button>
          <button
            type="button"
            className={align988TrendYearly ? "active" : ""}
            onClick={() => setAlign988TrendYearly(true)}
          >
            Yearly
          </button>
        </div>
      </div>
    </div>
  );

  const insightTrendPanel = (layer, type) => {
    if (!layer) return null;
    const isOutcome = type === "outcome";
    const is988 = is988Layer(layer);
    const subtitleParts = [];
    if (isOutcome) {
      const activeFields = BREAKDOWN_FILTER_FIELDS[dashboardBreakdownType] || [];
      activeFields.forEach((field) => {
        const label = OUTCOME_FILTER_FIELDS.find((item) => item.key === field)?.label || field;
        const values = selectedDashboardCategoryValues.length ? selectedDashboardCategoryValues.join(", ") : "None selected";
        subtitleParts.push(`${label}: ${values}`);
      });
    }

    const showOutcomeEmpty = isOutcome && !effectiveShowNationalTrend && !selectedRows.length;
    const show988Empty = is988 && !showNational988Trend && !selectedRows.length;
    const showLineWarning = isOutcome && outcomeTrendSeries.length > 6;
    const chartStatus = isOutcome
      ? outcomeTrendLoading
        ? "Loading outcome trends..."
        : outcomeTrendError || (showOutcomeEmpty ? "Use the Location dropdown to select one state, or turn on national trend." : "")
      : is988
        ? crisis988TrendLoading
          ? "Loading 988 KPI trends..."
          : crisis988TrendError || (show988Empty ? "Use the Location dropdown to select one state, or turn on national trend to view 988 KPI trends." : "")
        : "";
    const trendTitle = layer.trendTitle || (is988 ? "988 KPI trend" : layer.label);
    const trendSubtitle = isOutcome
      ? (subtitleParts.length ? subtitleParts.join(" | ") : "Overall trend")
      : is988
        ? KPI_988_BY_METRIC[explore988Metric]?.label || "In-State Answer Rate"
        : "Indicator trend";
    const showOutcomeTrendMessage = outcomeTrendMessage
      && !(isOutcome && layer.id === "suicideAdjustedRate" && /national adjusted trend is unavailable/i.test(outcomeTrendMessage));

    return (
      <div className={analysisRowClass("glass-card us-chart-card insight-chart-card insight-trend-panel")} key={layer.id}>
        <div className="panel-header insight-trend-header">
          <div>
            <h3>{trendTitle}</h3>
            <span>{trendSubtitle}</span>
          </div>
          {isOutcome ? insightOutcomeControls() : null}
          {is988 ? insight988Controls() : null}
        </div>
        {isOutcome ? (
          <>
            {showOutcomeTrendMessage && <p className="insight-helper-text">{outcomeTrendMessage}</p>}
            {showLineWarning && <p className="insight-helper-text">Too many lines may make the chart harder to read. Try fewer categories or states.</p>}
            {chartStatus ? (
              <div className={"insight-trend-empty " + (outcomeTrendError ? "error" : "")}>{chartStatus}</div>
            ) : outcomeTrendSeries.length ? (
              <Plot
                data={outcomeTrendTraces(outcomeTrendAxisYears)}
                layout={chartLayout("", layer, outcomeTrendAxisYears, sharedInsightHoverYear)}
                config={{ displayModeBar: true, responsive: true }}
                useResizeHandler
                className="us-chart-plot insight-trend-plot"
                onHover={handleInsightTrendHover}
                onUnhover={clearInsightTrendHover}
              />
            ) : (
              <div className="insight-trend-empty">{outcomeTrendMessage || "No outcome trend data is available for this selection."}</div>
            )}
          </>
        ) : is988 ? (
          <>
            {crisis988TrendMessage && <p className="insight-helper-text">{crisis988TrendMessage}</p>}
            {chartStatus ? (
              <div className={"insight-trend-empty " + (crisis988TrendError ? "error" : "")}>{chartStatus}</div>
            ) : crisis988TrendSeries.length ? (
              <Plot
                data={crisis988TrendTraces(crisis988TrendAxisYears)}
                layout={kpi988TrendLayout(explore988Metric, align988TrendYearly, crisis988TrendAxisYears, sharedInsightHoverYear)}
                config={{ displayModeBar: true, responsive: true }}
                useResizeHandler
                className="us-chart-plot insight-trend-plot"
                onHover={handleInsightTrendHover}
                onUnhover={clearInsightTrendHover}
              />
            ) : (
              <div className="insight-trend-empty">{crisis988TrendMessage || "No 988 KPI trend data is available for this selection."}</div>
            )}
          </>
        ) : selectedRows.length ? (
          <Plot
            data={historicalTracesForLayer(layer)}
            layout={chartLayout("", layer)}
            config={{ displayModeBar: true, responsive: true }}
            useResizeHandler
            className="us-chart-plot insight-trend-plot"
          />
        ) : (
          <div className="insight-trend-empty">Use the Location dropdown to select one state to view trends.</div>
        )}
      </div>
    );
  };

  const nationalTrendTraces = nationalMetric
    ? nationalMetric.segments.map((segment) => {
      const points = nationalMetric.points.filter((point) => point.segment === segment.id);
      return {
        type: "scatter",
        mode: points.length > 1 ? "lines+markers" : "markers",
        name: segment.label,
        x: points.map((point) => point.year),
        y: points.map((point) => point.value),
        customdata: points.map((point) => formatLayerValue(point.value, nationalMetric)),
        connectgaps: false,
        line: { width: 3, color: segment.color },
        marker: { size: 8, color: segment.color },
        hovertemplate: segment.label + "<br>%{x}: %{customdata}<extra></extra>",
      };
    })
    : [];
  const nationalTrendPanel = nationalMetric ? (
    <div className="glass-card us-chart-card insight-chart-card insight-trend-panel national-only-trend-panel">
      <div className="panel-header insight-trend-header">
        <div>
          <h3>{nationalMetric.label}</h3>
          <span>National NSDUH trend</span>
          <SourceMetadata meta={nationalTreatmentSourceMetaForSelectedYear} />
        </div>
      </div>
      <p className="national-only-message">Only the national estimates are publicly available without accessing and analyzing the raw data.</p>
      <p className="insight-helper-text national-only-comparability-note">{nationalMetric.comparabilityNote}</p>
      {nsduhTreatmentLoading ? (
        <div className="insight-trend-empty" role="status">Loading current estimates from SAMHSA's annual HTML tables...</div>
      ) : nsduhTreatmentError ? (
        <div className="insight-trend-empty error" role="alert">{nsduhTreatmentError}</div>
      ) : nationalMetric.points.length ? (
        <Plot
          data={nationalTrendTraces}
          layout={chartLayout("", nationalMetric, nationalMetric.points.map((point) => point.year))}
          config={{ displayModeBar: true, responsive: true }}
          useResizeHandler
          className="us-chart-plot insight-trend-plot"
        />
      ) : (
        <div className="insight-trend-empty">No live NSDUH treatment estimates are available.</div>
      )}
      {nationalMetric.warnings.length ? (
        <p className="insight-helper-text national-only-comparability-note">{nationalMetric.warnings.join(" ")}</p>
      ) : null}
      <div className="national-only-comparability-details">
        <strong>Comparability notes</strong>
        <ul>
          {nationalMetric.comparabilityDetails.map((detail) => <li key={detail}>{detail}</li>)}
        </ul>
      </div>
    </div>
  ) : null;

  const usMap = (layerId, compact = false, neutralSelectionOnly = false) => (
    <Plot
      key={`map-${dataDashboardView}-${layerId}-${compact ? "compact" : "full"}-${neutralSelectionOnly ? "neutral" : "data"}`}
      data={mapDataForLayer(layerId, neutralSelectionOnly)}
      layout={mapLayout(compact)}
      config={{ displayModeBar: false, responsive: true }}
      useResizeHandler
      className={`${compact ? "us-map-plot compact" : "us-map-plot"} dashboard-map-transition map-view-${dataDashboardView}`}
      onClick={(event) => toggleState(event?.points?.[0]?.location)}
    />
  );

  const selectedStateChips = selectedRows.length ? (
    <div className="state-selection-row data-dashboard-map-chips" aria-label="Selected states">
      {selectedRows.map((row, index) => (
        <button
          type="button"
          key={row.code}
          className="state-chip"
          style={{ borderColor: STATE_COLORS[index % STATE_COLORS.length] }}
          onClick={() => removeState(row.code)}
        >
          {row.name} x
        </button>
      ))}
    </div>
  ) : (
    <div className="state-selection-row data-dashboard-map-chips" aria-label="Selected states">
      <span className="state-selection-empty">{selectedStateLimit === 1 ? "Select one state." : "Select up to 2 states."}</span>
    </div>
  );

  const compactSelectionMapCard = (label) => (
    <aside className="glass-card us-map-card data-dashboard-selection-map-card">
      <div className="panel-header data-dashboard-selection-map-header">
        <div>
          <h3>{label}</h3>
          <span>Click the map or use Location</span>
        </div>
      </div>
      {selectedStateChips}
      {selectionLimitNotice}
      <div className="data-dashboard-selection-map-stage">
        {usMap(exploreLayer.id, true, true)}
      </div>
    </aside>
  );

  const exploreUsesOutcomeData = !isNationalOnlyMetric && isOutcomeLayer(exploreLayer);
  const exploreUses988Data = !isNationalOnlyMetric && is988Layer(exploreLayer);
  const exploreMapLoading = exploreUses988Data ? crisis988Loading : exploreUsesOutcomeData && outcomeLoading;
  const exploreHasNoOutcomeValues = exploreUsesOutcomeData && !outcomeLoading && !outcomeError && exploreYearValues.length === 0;
  const exploreHasNo988Values = exploreUses988Data && !crisis988Loading && !crisis988Error && exploreYearValues.length === 0;
  const exploreStatusMessage = exploreUses988Data
    ? crisis988Loading
      ? "Loading 988 KPI data..."
      : crisis988Error || (exploreHasNo988Values ? "No 988 KPI data found for the selected metric." : "")
    : outcomeLoading
      ? "Loading CDC outcome data..."
      : exploreHasNoOutcomeValues
        ? "No CDC outcome data found for the selected filters."
        : outcomeOptionsLoading && exploreUsesOutcomeData
          ? "Loading CDC filter options..."
          : "";
  const layerUnavailableMessage = exploreUses988Data
    ? crisis988Error
    : outcomeError && exploreUsesOutcomeData
      ? outcomeError
      : "";
  const exploreValueFormatter = (value) => formatLayerValue(value, exploreLayer, explore988Metric);
  const exploreSummaryRows = [
    {
      label: "National High",
      value: highestExploreYearRow
        ? highestExploreYearRow.name + " " + exploreValueFormatter(rateForSelectedYear(highestExploreYearRow, exploreLayer.id))
        : "No data",
    },
    {
      label: "National Low",
      value: lowestExploreYearRow
        ? lowestExploreYearRow.name + " " + exploreValueFormatter(rateForSelectedYear(lowestExploreYearRow, exploreLayer.id))
        : "No data",
    },
    {
      label: exploreUses988Data
        ? "Reporting State Average"
        : exploreLayer.id === "suicideCount" ? "National Total" : "National Average",
      value: exploreValueFormatter(nationalExploreYearValue),
    },
  ].filter((row) => exploreLayer.id !== "suicideAdjustedRate" || row.label !== "National Average");

  const selectedExploreRows = selectedRows.map((row) => ({
    code: row.code,
    label: row.name,
    value: exploreValueFormatter(rateForSelectedYear(row, exploreLayer.id)),
  }));
  const selectedExploreRow = selectedExploreRows[0] || null;
  const overviewNationalOutcomeSeries = Array.isArray(overviewNationalOutcomeData?.series)
    ? overviewNationalOutcomeData.series.find((series) => series.type === "national")
    : null;
  const overviewNationalOutcomePoint = (overviewNationalOutcomeSeries?.values || [])
    .find((point) => Number(point.year) === Number(mapYear));
  const overviewNationalOutcomeValue = overviewNationalOutcomeLoading
    ? "Loading..."
    : overviewNationalOutcomeError
      ? "Unavailable"
      : formatLayerValue(overviewNationalOutcomePoint?.value, exploreLayer);
  const showOverviewNationalValue = exploreUsesOutcomeData;

  const exploreBreakdownTitle = getBreakdownPanelTitle(exploreLayer, dashboardBreakdownType);
  const showExploreNationalComparisonControl = exploreUsesOutcomeData;
  const effectiveShowExploreNationalComparison = showExploreNationalComparisonControl && showExploreNationalComparison;
  const exploreBreakdownComparisonLabel = effectiveShowExploreNationalComparison
    ? selectedRows.length ? "National compared with selected states" : "National overview"
    : selectedRows.length ? "Selected states only" : "Select states to compare";
  const overallOutcomeComparisonData = {
    groups: [
      ...(effectiveShowExploreNationalComparison && isFiniteNumber(nationalExploreYearValue)
        ? [{
          type: "national",
          label: "United States",
          values: [{ category: "Overall", value: nationalExploreYearValue }],
        }]
        : []),
      ...selectedRows
        .map((row) => ({
          type: "state",
          label: row.name,
          values: [{ category: "Overall", value: rateForSelectedYear(row, exploreLayer.id) }],
        }))
        .filter((group) => isFiniteNumber(group.values[0]?.value)),
    ],
    message: selectedRows.length
      ? "No overall data is available for the selected state and year."
      : "Use the Location dropdown or map to select up to 2 states.",
  };
  const selectedBreakdownCategorySet = new Set(selectedDashboardCategoryValues);
  const visibleOutcomeBreakdownData = dashboardBreakdownType === "overall"
    ? overallOutcomeComparisonData
    : !selectedDashboardCategoryValues.length
      ? {
        year: Number(mapYear),
        layer: outcomeApiLayerFor(exploreLayer),
        breakdown_type: dashboardBreakdownType,
        groups: [],
        message: "Select one or more categories from the dropdown to view breakdown data.",
      }
      : outcomeBreakdownData
        ? {
          ...outcomeBreakdownData,
          groups: (Array.isArray(outcomeBreakdownData.groups) ? outcomeBreakdownData.groups : [])
            .filter((group) => effectiveShowExploreNationalComparison || group.type !== "national")
            .map((group) => ({
              ...group,
              values: (Array.isArray(group.values) ? group.values : [])
                .filter((item) => selectedBreakdownCategorySet.has(item.category)),
            }))
            .filter((group) => group.values.length),
          message: !effectiveShowExploreNationalComparison && !selectedRows.length
            ? showExploreNationalComparisonControl
              ? "Use the Location dropdown to select up to 2 states, or turn on national comparison to view breakdown data."
              : "Use the Location dropdown or map to select up to 2 states."
            : outcomeBreakdownData.message,
        }
        : outcomeBreakdownData;
  const selectionLimitNotice = stateSelectionMessage ? (
    <div className="state-selection-limit-message" role="status">{stateSelectionMessage}</div>
  ) : null;
  const exploreBreakdownPanel = exploreUsesOutcomeData ? (
    <section
      className="glass-card breakdown-chart-card explore-breakdown-panel"
      aria-label="Outcome breakdown comparison"
      ref={breakdownPanelRef}
    >
      <div className="breakdown-panel-heading">
        <div className="breakdown-panel-title-block">
          <h4>Breakdown Analysis</h4>
          <strong>{dashboardBreakdownType === "overall" ? "Overall" : exploreBreakdownTitle}</strong>
          {exploreBreakdownComparisonLabel === "Selected states only" ? null : <p>{exploreBreakdownComparisonLabel}</p>}
        </div>
        <div className="breakdown-panel-controls" aria-label="Breakdown panel controls">
          {renderSelectedBreakdownCategoryChips()}
          {exploreYearControl("toolbar-field year-field breakdown-year-field compare-year-field")}
          {showExploreNationalComparisonControl ? (
            <label className="breakdown-national-toggle">
              <input
                type="checkbox"
                checked={effectiveShowExploreNationalComparison}
                onChange={(event) => setShowExploreNationalComparison(event.target.checked)}
              />
              <span>Show national comparison</span>
            </label>
          ) : null}
        </div>
      </div>
      <BreakdownComparisonChart
        data={visibleOutcomeBreakdownData}
        layer={exploreLayer}
        breakdownType={dashboardBreakdownType}
        loading={dashboardBreakdownType !== "overall" && outcomeBreakdownLoading}
        error={dashboardBreakdownType !== "overall" ? outcomeBreakdownError : ""}
      />
    </section>
  ) : null;
  const explore988ComparisonLabel = showExploreNationalComparison
    ? selectedRows.length ? "National compared with selected states" : "National overview"
    : selectedRows.length ? "Selected states only" : "National comparison is off";
  const explore988ComparisonPanel = exploreUses988Data ? (
    <section className="glass-card breakdown-chart-card explore-breakdown-panel kpi-988-panel" aria-label="988 KPI comparison" ref={kpiComparisonPanelRef}>
      <div className="breakdown-panel-heading">
        <div className="breakdown-panel-title-block">
          <h4>988 KPI Comparison</h4>
          <strong>{KPI_988_BY_METRIC[explore988Metric]?.label || "988 KPI"}</strong>
          <p>{explore988ComparisonLabel} | {mapYear}</p>
        </div>
        <div className="breakdown-panel-controls" aria-label="988 KPI panel controls">
          {exploreYearControl("toolbar-field year-field breakdown-year-field compare-year-field")}
          <label className="breakdown-national-toggle">
            <input
              type="checkbox"
              checked={showExploreNationalComparison}
              onChange={(event) => setShowExploreNationalComparison(event.target.checked)}
            />
            <span>Show national comparison</span>
          </label>
        </div>
      </div>
      <Kpi988ComparisonChart
        data={crisis988ComparisonData}
        metric={explore988Metric}
        loading={crisis988ComparisonLoading}
        error={crisis988ComparisonError}
        includeNational={showExploreNationalComparison}
        selectedCount={selectedRows.length}
      />
    </section>
  ) : null;

  const mapSummaryRows = ["National Low", "National High"]
    .map((label) => exploreSummaryRows.find((row) => row.label === label))
    .filter(Boolean);
  const summaryCards = (
    <aside className="explore-map-summary-panel" aria-label="Overview map summary">
      <div className="explore-summary-heading">
        <span>Summary</span>
      </div>
      <div className="explore-map-summary-cards">
        {mapSummaryRows.map((item) => (
          <article key={item.label} className="glass-card forecast-card">
            <h4>{item.label}</h4>
            <p>{item.value}</p>
          </article>
        ))}
        {showOverviewNationalValue ? (
          <article className="glass-card forecast-card national-value-card">
            <h4>National value</h4>
            <p>{overviewNationalOutcomeValue}</p>
          </article>
        ) : null}
        {selectedExploreRow ? (
          <button type="button" className="glass-card selected-state-feature-card" onClick={() => removeState(selectedExploreRow.code)}>
            <span>Selected state: {selectedExploreRow.label}</span>
            <strong>{selectedExploreRow.value}</strong>
            <small>Click to clear selection</small>
          </button>
        ) : (
          <p className="selected-state-empty selected-state-empty-summary">Click a state on the map to show its {exploreLayer.label.toLowerCase()} data here.</p>
        )}
      </div>
    </aside>
  );

  const nationalYearPoint = nationalMetric?.points.find((point) => Number(point.year) === Number(mapYear));
  const nationalOverviewPanel = nationalMetric ? (
    <section className="explore-map-summary-layout fade-in-up delay-2 national-only-overview-layout">
      <div className="explore-main-column">
        <div className="glass-card us-dashboard-panel national-only-overview-panel">
          <div className="panel-header us-panel-header">
            <div className="explore-map-title-row">
              <div className="explore-map-title-copy">
                <h3>{nationalMetric.label}</h3>
                <SourceMetadata meta={nationalTreatmentSourceMetaForSelectedYear} />
              </div>
              {nationalMetric.points.length ? (
                <div className="map-year-header-slot">
                  {exploreYearControl("toolbar-field year-field map-year-control map-year-header-control")}
                </div>
              ) : null}
            </div>
          </div>
          <div className="national-only-overview-body">
            {nsduhTreatmentLoading ? (
              <div className="insight-trend-empty" role="status">Loading current estimates from SAMHSA's annual HTML tables...</div>
            ) : nsduhTreatmentError ? (
              <div className="insight-trend-empty error" role="alert">{nsduhTreatmentError}</div>
            ) : nationalYearPoint ? (
              <>
                <article className="national-only-value-card">
                  <span>National estimate</span>
                  <strong>{formatLayerValue(nationalYearPoint.value, nationalMetric)}</strong>
                  <small>{mapYear} NSDUH estimate</small>
                </article>
                <div className="national-only-overview-copy">
                  <h4>National Data</h4>
                  <p>Only the national estimates are publicly available without accessing and analyzing the raw data.</p>
                  <ul>
                    <li>Trends and comparison are disabled because year-to-year comparisons may not be suitable, as question wording changed from year to year.</li>
                  </ul>
                  <p><strong>What this percentage represents:</strong> the share of adults aged 18 or older who had serious thoughts of suicide in the past year and received any mental health treatment during that same year.</p>
                  <p>{nationalMetric.definition}</p>
                </div>
              </>
            ) : (
              <div className="insight-trend-empty">No live NSDUH treatment estimate is available for this year.</div>
            )}
          </div>
          {nationalMetric.warnings.length ? (
            <p className="insight-helper-text national-only-comparability-note">{nationalMetric.warnings.join(" ")}</p>
          ) : null}
        </div>
      </div>
    </section>
  ) : null;

  const explorePanel = isNationalOnlyMetric ? nationalOverviewPanel : (
    <section className="explore-map-summary-layout fade-in-up delay-2">
      <div className="explore-main-column">
        <div className="glass-card us-dashboard-panel">
          <div className="panel-header us-panel-header">
            <div className="explore-map-title-row">
              <div className="explore-map-title-copy">
                <h3>{exploreLayer.mapTitle}</h3>
                {layerUnavailableMessage ? <span>{layerUnavailableMessage}</span> : null}
                <SourceMetadata meta={exploreUses988Data ? DATA_SOURCE_META.crisis988 : DATA_SOURCE_META.suicide} />
              </div>
              <div className="map-year-header-slot">
                {exploreYearControl("toolbar-field year-field map-year-control map-year-header-control")}
              </div>
            </div>
          </div>
          <div className="explore-map-panel-body">
            <div className="explore-map-loading-stage" aria-busy={exploreMapLoading}>
              {usMap(exploreLayer.id, false)}
              {exploreMapLoading ? (
                <div className="map-loading-overlay" role="status" aria-live="polite">
                  <div className="map-loading-card">
                    <span className="map-loading-spinner" aria-hidden="true" />
                    <strong>Loading map data</strong>
                  </div>
                </div>
              ) : null}
            </div>
            {summaryCards}
          </div>
          {exploreStatusMessage && (
            <div className="outcome-status-message" role="status">{exploreStatusMessage}</div>
          )}
          {selectionLimitNotice}
        </div>
      </div>
    </section>
  );

  const comparePanel = isNationalOnlyMetric ? (
    <section className="data-dashboard-compare-layout fade-in-up delay-2">
      <article className="glass-card national-only-unavailable-card" role="status">
        <span>National Data</span>
        <h3>State comparison unavailable</h3>
        <p>Comparison is unavailable because this measure is reported only at the national level.</p>
        <p>Select a state-level measure to compare states.</p>
      </article>
    </section>
  ) : (
    <section className="data-dashboard-compare-layout fade-in-up delay-2">
      <div className="data-dashboard-side-map-layout data-dashboard-compare-body">
        {compactSelectionMapCard("Compare states")}
        <div className="data-dashboard-compare-stack">
          {exploreBreakdownPanel}
          {explore988ComparisonPanel}
        </div>
      </div>
    </section>
  );

  const insightPanel = isNationalOnlyMetric ? (
    <section className="insight-redesigned-layout fade-in-up delay-2">
      <div className="insight-trend-stack insight-trend-stack-single national-only-trend-stack">
        {nationalTrendPanel}
      </div>
    </section>
  ) : (
    <section className="insight-redesigned-layout fade-in-up delay-2">
      <div className="data-dashboard-side-map-layout data-dashboard-trends-body">
        {compactSelectionMapCard("Trend states")}
        <div className="insight-trend-stack insight-trend-stack-single">
          {insightTrendPanel(selectedTrendLayer, selectedTrendType)}
        </div>
      </div>
    </section>
  );

  const scrollToDashboardSection = (tabId) => {
    setActiveTab(tabId);
    window.requestAnimationFrame(() => {
      document.getElementById("dashboard-section-" + tabId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const scrollToSelectedMeasures = () => {
    setActiveTab("overview");
    window.requestAnimationFrame(() => {
      document.getElementById("selected-measure-cards")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const applyDataDashboardSelection = (value) => {
    if (!value) return false;

    if (value === NSDUH_TREATMENT_SERIES.selectionValue) {
      setNationalMetricId(NSDUH_TREATMENT_SERIES.id);
      setDataDashboardView("overview");
      setExploreLayerId(DEFAULT_LAYER_ID);
      setSelectedStates([]);
      setStateSelectionMessage("");
      setBreakdownType("overall");
      setDashboardCategorySelections(emptyDashboardCategorySelections());
      setCategoryDropdownOpen(false);
      setBreakdownSelectionExpanded(false);
      return true;
    }

    if (value.startsWith("kpi:")) {
      const metric = value.replace("kpi:", "");
      if (!KPI_988_BY_METRIC[metric]) return false;
      setNationalMetricId(null);
      setExploreLayerId(DEFAULT_INDICATOR_LAYER_ID);
      setExplore988Metric(metric);
      return true;
    }

    if (value.startsWith("layer:")) {
      const layerId = value.replace("layer:", "");
      if (!MAP_LAYER_BY_ID[layerId]) return false;
      setNationalMetricId(null);
      setExploreLayerId(layerId);
      return true;
    }

    return false;
  };

  const showOverviewMetricSoonMessage = () => {
    clearOverviewMetricMessageTimer();
    setOverviewMetricMessage("Data will be added soon.");
    overviewMetricMessageTimerRef.current = window.setTimeout(() => {
      setOverviewMetricMessage("");
      overviewMetricMessageTimerRef.current = null;
    }, 3200);
  };

  const handleOverviewMetricClick = (metric) => {
    if (!DATA_READY_SELECTED_MEASURES.has(metric.title) || !metric.dataSelectionValue) {
      showOverviewMetricSoonMessage();
      return;
    }

    clearOverviewMetricMessageTimer();
    setOverviewMetricMessage("");
    applyDataDashboardSelection(metric.dataSelectionValue);
    setDataDashboardView("overview");
    scrollToDashboardSection("dataDashboard");
  };

  const overviewPanel = (
    <section className="landing-page fade-in-up" ref={overviewRevealRef}>
      <section className="strategy-intro-panel" aria-label="National Strategy for Suicide Prevention (National Strategy) overview">
        <article className="strategy-intro-card strategy-national-card">
          <div className="strategy-card-heading">
            <span className="landing-kicker strategy-kicker">
              <em>National Strategy for Suicide Prevention (National Strategy)</em>
            </span>
            <span className="strategy-card-meta">10-year national framework</span>
          </div>
          <h1>A national, whole-of-society approach to suicide prevention</h1>
          <p>
            <em>National Strategy for Suicide Prevention (National Strategy)</em> is a bold, 10-year, comprehensive, whole-of-society approach to suicide prevention that provides concrete recommendations for addressing gaps in the suicide prevention field.
          </p>
          <div className="strategy-intro-actions">
            <a className="strategy-link strategy-link-primary" href={NATIONAL_STRATEGY_URL} target="_blank" rel="noreferrer">
              Learn more about National Strategy <span aria-hidden="true">↗</span>
            </a>
          </div>
        </article>

        <article className="strategy-intro-card strategy-direction-card">
          <div className="strategy-card-heading">
            <span className="landing-kicker strategy-kicker">Strategic Direction 2</span>
            <span className="strategy-card-meta">Focus of this dashboard</span>
          </div>
          <h2>Treatment and Crisis Services</h2>
          <div className="strategy-direction-summary">
            <p className="strategy-direction-description">
              Strategic Direction 2 supports integrating effective suicide prevention services into health care and improving the quality and accessibility of crisis care services across all communities.
            </p>
            <p className="strategy-metrics-note">
              The National Action Alliance for Suicide Prevention, in collaboration with the federal government, monitors longer-term core indicators for <em>National Strategy</em>. This dashboard highlights selected measures related to Strategic Direction 2.
            </p>
          </div>
          <div className="strategy-intro-actions">
            <a className="strategy-link strategy-link-primary" href="#overview-selected-measures">
              Learn more about selected measures <span aria-hidden="true">↓</span>
            </a>
          </div>
        </article>
      </section>

      <section id="overview-selected-measures" className="overview-metric-showcase" aria-label="Metric selection framework and selected measures">
        <div className="overview-metric-showcase-header">
          <h2>Metric selection process</h2>
          <p>
            The HopeCounts metric-selection team identified the following measures using three metric-selection framework areas: Care Practices, Context &amp; Tracking, and Coverage &amp; Reach.
          </p>
        </div>
        <div className="metric-selection-framework-grid" aria-label="Metric selection framework areas">
          {METRIC_SELECTION_FRAMEWORKS.map((framework) => (
            <article className={`metric-selection-framework-card metric-selection-framework-card--${framework.id}`} key={framework.id}>
              <div className="metric-selection-framework-card-copy">
                <h3>{framework.title}</h3>
                {framework.prompts.map((prompt) => <p key={prompt}>{prompt}</p>)}
              </div>
              <MetricSelectionFrameworkIcon type={framework.id} />
            </article>
          ))}
        </div>
        <section className="framework-scoring-characteristics" aria-labelledby="framework-scoring-characteristics-title">
          <h3 id="framework-scoring-characteristics-title">Criteria for selecting our measures</h3>
          <div className="framework-scoring-characteristics-grid">
            {[SCORING_CHARACTERISTICS.slice(0, 6), SCORING_CHARACTERISTICS.slice(6)].map((column, index) => (
              <ul className="framework-scoring-characteristics-list" key={index}>
                {column.map((characteristic) => (
                  <li key={characteristic.label}>
                    <strong>{characteristic.label}:</strong> {characteristic.description}
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </section>
        <section id="selected-measure-cards" className="selected-measures-section" aria-labelledby="selected-measures-title">
          <div className="selected-measures-header">
            <h3 id="selected-measures-title">Selected measures</h3>
            {overviewMetricMessage ? (
              <div className="overview-metric-status" role="status" aria-live="polite">{overviewMetricMessage}</div>
            ) : null}
          </div>
          <div className="selected-measures-groups">
            {SELECTED_MEASURE_COLUMNS.map((column) => (
              <div
                className={`selected-measure-column selected-measure-column--${column.frameworkId}`}
                key={column.id}
              >
                <div className="selected-measure-column-heading">
                  <h4>{column.title}</h4>
                </div>
                <div className="selected-measure-column-cards">
                  {column.metrics.map((metric, metricIndex) => (
                    <button
                      type="button"
                      className={`overview-feature-card selected-measure-card selected-measure-card--${metric.frameworkId}`}
                      key={metric.title}
                      onClick={() => handleOverviewMetricClick(metric)}
                      aria-label={DATA_READY_SELECTED_MEASURES.has(metric.title) && metric.dataSelectionValue ? "Open data dashboard for " + metric.title : metric.title + ". Data will be added soon."}
                    >
                      <div className="overview-feature-card-top">
                        <div className="overview-feature-number">{String(metricIndex + 1).padStart(2, "0")}</div>
                        <strong>{metric.eyebrow}</strong>
                      </div>
                      <h3>{metric.title}</h3>
                      <p>{metric.body}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </section>

    </section>
  );

  const selectedStateResources = selectedResourceState ? STATE_RESOURCE_LINKS[selectedResourceState]?.resources || [] : [];

  const resourcesPanel = (
    <section className="resources-panel fade-in-up delay-2">
      <div className="resource-grid">
        <article className="glass-card resource-card resource-card-wide">
          <h3>Federal related information resources</h3>
          <div className="federal-resource-groups">
            {FEDERAL_RESOURCE_GROUPS.map((group) => (
              <section className="federal-resource-group" key={group.category}>
                <h4>{group.category}</h4>
                <ul className="resource-link-list">
                  {group.links.map((link) => (
                    <li key={link.url}>
                      <a href={link.url} target="_blank" rel="noreferrer">{link.description}</a>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </article>
        <article className="glass-card resource-card">
          <h3>State resources</h3>
          <label className="resource-select-label">
            Select a state
            <select value={selectedResourceState} onChange={(event) => setSelectedResourceState(event.target.value)}>
              <option value="">Choose a state</option>
              {Object.entries(STATE_RESOURCE_LINKS).map(([value, state]) => (
                <option key={value} value={value}>{state.label}</option>
              ))}
            </select>
          </label>
          {selectedResourceState ? (
            <ul className="resource-link-list state-resource-list">
              {selectedStateResources.map((resource) => (
                <li key={resource.url}>
                  <a href={resource.url} target="_blank" rel="noreferrer">{resource.label}</a>
                </li>
              ))}
            </ul>
          ) : (
            <p>Select Colorado or Arizona to view available state suicide surveillance resources.</p>
          )}
        </article>
        <article className="glass-card resource-card">
          <h3>Crisis resources</h3>
          <p>For immediate danger in the United States, call 911. For crisis support, call or text 988.</p>
        </article>
      </div>
    </section>
  );

  const storiesPanel = (
    <section className="stories-panel fade-in-up delay-2">
      <div className="stories-intro">
        <span>Shared experiences</span>
        <h2>Community success stories</h2>
        <p>
          These story previews center help-seeking, recovery, and connection. They are included as reflective material alongside the data, not as crisis support.
        </p>
      </div>
      <div className="story-video-grid">
        {STORY_CARDS.map((story) => (
          <article className="story-video-card" key={story.imageUrl}>
            <div className="story-image-frame">
              <img src={story.imageUrl} alt={story.title} loading="lazy" />
              <span className="story-youtube-badge" aria-hidden="true" />
            </div>
            <div className="story-video-meta">
              <span>Story preview</span>
              <h3>{story.title}</h3>
            </div>
          </article>
        ))}
      </div>
    </section>
  );

  const currentTabTitle = activeCopy?.title || "National Strategy Monitoring Prototype";
  const currentDataDashboardView = DATA_DASHBOARD_VIEWS.find((view) => view.id === dataDashboardView) || DATA_DASHBOARD_VIEWS[0];
  const selectedDashboardMetric = nationalMetric || exploreLayer;
  const currentTabContext = activeTab === "dataDashboard"
    ? dataDashboardView === "trends"
      ? {
        tab: "Data Dashboard - Trends",
        contextName: currentDataDashboardView.title,
        selectedStateLimit,
        selectedStateCount: selectedRows.length,
        selectedStates: selectedRows.map((row) => ({ state: row.name, code: row.code })),
        layers: isNationalOnlyMetric
          ? [{
            layer: nationalMetric.label,
            metric: nationalMetric.metric,
            geography: nationalMetric.geography,
            nationalSeries: nationalMetric.points,
            comparabilityNote: nationalMetric.comparabilityNote,
          }]
          : [selectedTrendLayer].filter(Boolean).map((layer) => insightHistoryForLayer(layer)),
      }
      : {
        tab: `Data Dashboard - ${currentDataDashboardView.label}`,
        contextName: currentDataDashboardView.title,
        selectedLayer: selectedDashboardMetric.label,
        metric: selectedDashboardMetric.metric,
        geography: isNationalOnlyMetric ? nationalMetric.geography : undefined,
        nationalOnly: isNationalOnlyMetric,
        selectedYear: mapYear,
        selectedStateLimit,
        selectedStateCount: selectedRows.length,
        selectedStates: selectedRows.map((row) => ({ state: row.name, code: row.code })),
      }
    : {
      tab: currentTabTitle,
      contextName: currentTabTitle,
    };
  const chatContext = {
    tab: activeTab,
    currentTabTitle,
    selectedContextTitle: currentTabTitle,
    selectedContext: currentTabContext,
  };

  useEffect(() => {
    const sections = DASHBOARD_SECTION_NAV
      .map((section) => section.id)
      .map((id) => document.getElementById(`dashboard-section-${id}`))
      .filter(Boolean);

    if (!sections.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const nextTab = visibleEntry?.target?.dataset?.dashboardSection;
        if (nextTab) setActiveTab(nextTab);
      },
      {
        root: null,
        rootMargin: "-18% 0px -58% 0px",
        threshold: [0.08, 0.18, 0.32, 0.5, 0.72],
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const sectionHero = (tabId, extraAction = null) => {
    const copy = NAV_COPY[tabId];
    if (!copy) return null;
    return (
      <section className="dashboard-hero fade-in-up">
        <h1 className="dashboard-title">{copy.title}</h1>
        <div className="dashboard-hero-row">
          <p className="dashboard-lede">{copy.description}</p>
          {extraAction}
        </div>
      </section>
    );
  };

  const selectedLocationSummary = isNationalOnlyMetric ? "National only" : selectedRows.length ? selectedRows.map((row) => row.name).join(" + ") : "National";
  const selectedLocationValue = isNationalOnlyMetric ? "national" : selectedRows.length ? selectedRows.map((row) => row.code).join("|") : "national";
  const handleDataDashboardLocationChange = (event) => {
    const code = event.target.value;
    if (isNationalOnlyMetric || !code) return;
    if (code === "national") {
      setSelectedStates([]);
      return;
    }
    if (code === selectedLocationValue || code.includes("|")) return;
    setSelectedStates((current) => {
      if (current.includes(code)) return current;
      if (selectedStateLimit === 1) return [code];
      return [...current.slice(-(selectedStateLimit - 1)), code];
    });
  };

  const handleDataDashboardDataSelectionChange = (event) => {
    const value = event.target.value;
    if (!value || value === selectedDataSelectionValue) return;
    dashboardCategorySelectionTouchedRef.current = false;
    applyDataDashboardSelection(value);
  };

  const handleDashboardBreakdownChange = (event) => {
    dashboardCategorySelectionTouchedRef.current = false;
    updateOutcomeFilter("breakdownType", event.target.value);
    setCategoryDropdownOpen(false);
    setBreakdownSelectionExpanded(false);
  };
  const handleDashboardCategoryChange = (event) => {
    if (!insightCategoryField) return;
    updateOutcomeFilter(insightCategoryField.key, event.target.value);
  };
  const showDashboardCategorySelect = !isNationalOnlyMetric && isOutcomeLayer(exploreLayer) && dashboardBreakdownType !== "overall" && insightCategoryField;
  const showDashboardSingleCategorySelect = showDashboardCategorySelect && dataDashboardView === "overview";
  const showDashboardMultiCategorySelect = showDashboardCategorySelect && dataDashboardView !== "overview";
  const categoryDropdownValue = effectiveOutcomeCategoryValue;

  const completeDashboardTour = () => {
    setPersistentFlag(DASHBOARD_TOUR_STORAGE_KEY);
    setTourStepIndex(-1);
  };

  const showDashboardInfoMessage = (message) => {
    clearDashboardInfoMessageTimer();
    setDashboardInfoMessage(message);
    dashboardInfoMessageTimerRef.current = window.setTimeout(() => {
      setDashboardInfoMessage("");
      dashboardInfoMessageTimerRef.current = null;
    }, 4200);
  };

  const handleSkipDashboardTour = () => {
    completeDashboardTour();
    showDashboardInfoMessage("Tutorial skipped. You can always use the help button to restart it.");
  };

  const handleRestartDashboardTour = () => {
    setActiveTab("overview");
    setTourSpotlight(null);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setTourStepIndex(0);
  };

  const advanceDashboardTour = () => {
    if (tourStepIndex >= DASHBOARD_TOUR_STEPS.length - 1) {
      completeDashboardTour();
      return;
    }
    setTourStepIndex((index) => index + 1);
  };

  const tourLayerStyle = tourSpotlight
    ? {
        "--tour-spotlight-top": `${tourSpotlight.top}px`,
        "--tour-spotlight-left": `${tourSpotlight.left}px`,
        "--tour-spotlight-width": `${tourSpotlight.width}px`,
        "--tour-spotlight-height": `${tourSpotlight.height}px`,
      }
    : {};

  const dashboardTour = activeTourStep ? (
    <div className={`dashboard-tour-layer dashboard-tour-step-${activeTourStep.id}`} style={tourLayerStyle}>
      {tourSpotlight ? <div className="dashboard-tour-spotlight" aria-hidden="true" /> : null}
      <article
        className={`dashboard-tour-card dashboard-tour-card-${activeTourStep.id}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`dashboard-tour-title-${activeTourStep.id}`}
      >
        <span className="dashboard-tour-progress">Step {tourStepIndex + 1} of {DASHBOARD_TOUR_STEPS.length}</span>
        <h2 id={`dashboard-tour-title-${activeTourStep.id}`}>{activeTourStep.title}</h2>
        <p>{activeTourStep.body}</p>
        <div className="dashboard-tour-actions">
          <button type="button" className="dashboard-tour-secondary" onClick={handleSkipDashboardTour}>Skip</button>
          <button type="button" className="dashboard-tour-primary" onClick={advanceDashboardTour}>
            {tourStepIndex >= DASHBOARD_TOUR_STEPS.length - 1 ? "Finish" : "Next"}
          </button>
        </div>
      </article>
    </div>
  ) : null;

  const aboutUsPanel = (
    <section className="about-us-panel fade-in-up delay-1">
      <div className="about-us-column about-us-client-column">
        <a className="about-us-logo-link about-us-client-logo about-us-action-alliance-link" href={ABOUT_US_URL} target="_blank" rel="noreferrer">
          The National Action Alliance for Suicide Prevention (Action Alliance)
        </a>
      </div>
      <div className="about-us-column about-us-disc-column">
        <a
          className="about-us-disc-logo"
          href="https://www.ou.edu/disc"
          target="_blank"
          rel="noreferrer"
          aria-label="Visit the Data Institute for Societal Challenges website"
        >
          <img src={DISC_LOGO_SRC} alt="Data Institute for Societal Challenges logo" />
        </a>
        <p>
          This prototype is designed by the <a href="https://www.ou.edu/disc" target="_blank" rel="noreferrer">Data Institute for Societal Challenges (DISC)</a> under an agreement with the National Alliance for Suicide Prevention.
        </p>
      </div>
    </section>
  );

  const dataDashboardPanel = (
    <section className="data-dashboard-section fade-in-up delay-2">
      <div className="data-dashboard-title-row">
        <h2 className="data-dashboard-title">Data Dashboard</h2>
        <button type="button" className="data-dashboard-selected-measures-button" onClick={scrollToSelectedMeasures} aria-label="Return to selected measures"><span className="data-dashboard-selected-measures-icon" aria-hidden="true">↑</span><span className="data-dashboard-selected-measures-label">Return to selected measures</span></button>
      </div>
      <div className="data-dashboard-nav glass-card">
        <div className="data-dashboard-controls">
          <div className="data-dashboard-primary-row">
            <label className="data-dashboard-location-control">
              <span>Location</span>
              <select value={selectedLocationValue} onChange={handleDataDashboardLocationChange} disabled={isNationalOnlyMetric}>
                {isNationalOnlyMetric ? (
                  <option value="national">National only</option>
                ) : (
                  <>
                    <option value={selectedLocationValue}>{selectedLocationSummary}</option>
                    {selectedRows.length ? <option value="national">National</option> : null}
                    {STATES.map((state) => (
                      <option key={state.code} value={state.code}>{state.name}</option>
                    ))}
                  </>
                )}
              </select>
            </label>
            <label className="data-dashboard-location-control data-dashboard-data-control">
              <span>Data selection</span>
              <select value={selectedDataSelectionValue} onChange={handleDataDashboardDataSelectionChange}>
                {DATA_SELECTION_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <div className="data-dashboard-view-tabs" role="tablist" aria-label="Data dashboard views">
              {DATA_DASHBOARD_VIEWS.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  role="tab"
                  aria-selected={dataDashboardView === view.id}
                  className={`data-dashboard-view-button ${dataDashboardView === view.id ? "active" : ""}`}
                  disabled={isNationalOnlyMetric && ["trends", "compare"].includes(view.id)}
                  title={isNationalOnlyMetric && ["trends", "compare"].includes(view.id) ? `${view.label} is unavailable for this national-only measure.` : undefined}
                  onClick={() => setDataDashboardView(view.id)}
                >
                  {view.label}
                </button>
              ))}
            </div>
            <label className="data-dashboard-location-control data-dashboard-breakdown-control">
              <span>Breakdown</span>
              <select
                value={dashboardBreakdownType}
                onChange={handleDashboardBreakdownChange}
                disabled={isNationalOnlyMetric || !isOutcomeLayer(exploreLayer) || outcomeOptionsLoading}
              >
                {(dashboardBreakdownOptions.length ? dashboardBreakdownOptions : ["overall"]).map((option) => (
                  <option key={option} value={option}>{BREAKDOWN_LABELS[option] || option.replaceAll("_", " ")}</option>
                ))}
              </select>
            </label>
            {showDashboardSingleCategorySelect ? (
              <label className="data-dashboard-location-control data-dashboard-subbreakdown-control">
                <span>{insightCategoryField.label}</span>
                <select
                  value={categoryDropdownValue}
                  onChange={handleDashboardCategoryChange}
                  disabled={!insightCategoryOptions.length}
                >
                  {insightCategoryOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {showDashboardMultiCategorySelect ? (
              <div className="data-dashboard-location-control data-dashboard-subbreakdown-control data-dashboard-checkbox-dropdown" ref={categoryDropdownRef}>
                <span>Categories</span>
                <div className="breakdown-multi-select">
                  <button
                    type="button"
                    className="breakdown-multi-select-button"
                    aria-haspopup="true"
                    aria-expanded={categoryDropdownOpen}
                    aria-label={`Select ${BREAKDOWN_LABELS[dashboardBreakdownType] || insightCategoryField.label} categories`}
                    disabled={dashboardCategoryDropdownDisabled}
                    onClick={() => setCategoryDropdownOpen((open) => !open)}
                  >
                    <span className="breakdown-multi-select-summary">{dashboardCategoryDropdownSummary}</span>
                    <span className="breakdown-multi-select-caret" aria-hidden="true">v</span>
                  </button>
                  {categoryDropdownOpen && !dashboardCategoryDropdownDisabled ? (
                    <div className="breakdown-multi-select-menu" role="group" aria-label={`${BREAKDOWN_LABELS[dashboardBreakdownType] || insightCategoryField.label} category options`}>
                      {insightCategoryOptions.map((option) => {
                        const checked = selectedDashboardCategoryValues.includes(option);
                        const optionDisabled = !checked && selectedDashboardCategoryValues.length >= MAX_DASHBOARD_CATEGORY_SELECTIONS;
                        return (
                          <label
                            className={`breakdown-multi-select-option ${checked ? "selected" : ""} ${optionDisabled ? "disabled" : ""}`}
                            key={option}
                            title={option}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={optionDisabled}
                              onChange={() => toggleDashboardCategorySelection(option)}
                            />
                            <span>{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div key={dataDashboardView} className={`data-dashboard-panel data-dashboard-panel-${dataDashboardView}`} role="tabpanel">
        {dataDashboardView === "overview" ? explorePanel : dataDashboardView === "trends" ? insightPanel : comparePanel}
      </div>
    </section>
  );

  return (
    <div id="dashboard" className={`dashboard-shell dashboard-tab-${activeTab} ${dashboardTourActive ? "dashboard-tour-active" : ""}`}>
      <TopToolbar
        activeTab={activeTab}
        onChangeTab={scrollToDashboardSection}
        currentUser={currentUser}
        onLoginClick={onLoginClick}
        onLogout={onLogout}
      />

      <aside className="crisis-support-strip" aria-label="Crisis support information">
        If you or someone you know is experiencing a suicide or mental health related crisis, please call or text 988 or chat{" "}
        <a href="https://988lifeline.org/" target="_blank" rel="noreferrer">988Lifeline.org</a>.
      </aside>

      <nav className="section-rail" aria-label="Dashboard section shortcuts">
        {DASHBOARD_SECTION_NAV.map((section, index) => (
          <button
            key={section.id}
            type="button"
            className={`section-rail-button ${activeTab === section.id ? "active" : ""}`}
            onClick={() => scrollToDashboardSection(section.id)}
            aria-label={`Scroll to ${section.label}`}
            aria-current={activeTab === section.id ? "true" : undefined}
          >
            <span className="section-rail-number">{String(index + 1).padStart(2, "0")}</span>
            <span className="section-rail-dot" aria-hidden="true" />
            <span className="section-rail-label">{section.label}</span>
          </button>
        ))}
      </nav>

      <div className="dashboard-layout">
        <main className="main-content one-page-dashboard">
          <section id="dashboard-section-overview" data-dashboard-section="overview" className="one-page-section one-page-section-overview" aria-label="Strategic Direction 2">
            {overviewPanel}
          </section>

          <section id="dashboard-section-dataDashboard" data-dashboard-section="dataDashboard" className="one-page-section one-page-section-data-dashboard" aria-label="Data Dashboard">
            {dataDashboardPanel}
          </section>

          <section id="dashboard-section-resources" data-dashboard-section="resources" className="one-page-section one-page-section-resources" aria-label="Resources">
            {sectionHero("resources")}
            {resourcesPanel}
          </section>

          <section id="dashboard-section-stories" data-dashboard-section="stories" className="one-page-section one-page-section-stories" aria-label="Stories of Hope">
            {sectionHero("stories")}
            {storiesPanel}
          </section>

          <section id="dashboard-section-aboutUs" data-dashboard-section="aboutUs" className="one-page-section one-page-section-about-us" aria-label="About Us">
            <section className="dashboard-hero fade-in-up">
              <h1 className="dashboard-title">About Us</h1>
            </section>
            {aboutUsPanel}
          </section>
        </main>
      </div>

      {dashboardInfoMessage ? (
        <div className="dashboard-info-toast" role="status" aria-live="polite">{dashboardInfoMessage}</div>
      ) : null}

      <ChatFab
        context={chatContext}
        pinnedMessage={CHAT_PINNED_MESSAGES[activeTab] || CHAT_PINNED_MESSAGES.dataDashboard}
        showContextInfo={activeTab === "dataDashboard"}
        actions={[
          { id: "restart-dashboard-tour", label: "Restart tutorial", onClick: handleRestartDashboardTour, closeOnClick: true },
        ]}
        enabled={CHAT_ENABLED}
      />
      {dashboardTour}
    </div>
  );
}

export default Dashboard;
